import {
    PAPER_WHITE_AVATAR_HUE,
    RELIC_RUN_DESCRIPTION,
    RELIC_RUN_MODE,
    MAZE_REVEAL_RADIUS,
    isMazePositionRevealed,
    normalizeSourceText,
    orderRelicRunParticipantsByTime,
    tokenizeSourceText,
    type GridPosition,
    type MazeTileView,
    type PlayerMazeView,
    type RoomParticipantView,
    type RoomView
} from "../../shared/game";
import "./browserCompatibility";
import { appendCodexDebugLog } from "./codexDebugLog";
import { createCapyShieldElement, setCapyShieldHue } from "./capyShield";
import { followRelicRunner, startRelicCamera } from "./relicRunCamera";
import type { RelicCourseBounds } from "../../shared/relicRunCourse";
import { localizeModeDescription, t } from "./i18n";
import {
    MazeCanvasRenderer,
    type MazeBoardRenderTile,
    type MazeCanvasFramePerfSample,
    type MazeCanvasRendererDiagnostics
} from "./mazeCanvasRenderer";
import {
    getPlayerRosterDisplayName,
    getPlayerRosterListClass,
    getPlayerRosterPlacementClass,
    getPlayerRenamePendingClass,
    renderPlayerRosterActionsMenu,
    syncPlayerRosterLayout
} from "./playerRoster";
import {
    downloadPlayerHistory,
    renderPlayerHistoryHeaderMenu
} from "./playerHistoryDownload";
import { applySiteTuningCssVariables, SITE_TUNING } from "./siteTuning";

type MazeViewOptions = {
    onMove: (target: GridPosition) => void;
    onReady: () => void;
    onReplay: () => void;
    onPromptRenameParticipant?: (sessionId: string) => void;
    onKickParticipant?: (sessionId: string) => void;
};

type MazeMovePerfSample = {
    id: number;
    source: string;
    tileKey: string;
    target: GridPosition;
    startedAt: number;
    phases: Record<string, number>;
    loggedAwaitingConfirmation: boolean;
};

type BufferedMoveIntent = {
    queuedAt: number;
    source: string;
    target: GridPosition;
    tileKey: string;
};

type TouchSwipeIntent = {
    pointerId: number;
    startX: number;
    startY: number;
    handled: boolean;
    lastX: number;
    lastY: number;
    distanceSinceCommit: number;
    cameraMoved: boolean;
};

type PassageTokenSlice = {
    end: number;
    start: number;
};

type PassagePreview = {
    text: string;
    truncated: boolean;
};

type PassagePreviewCache = {
    normalizedPassage: string;
    previewsByWordIndex: Map<number, PassagePreview>;
    sourceText: string;
    slices: PassageTokenSlice[];
};

const CELL_SIZE = SITE_TUNING.relicRun.board.cellSizePx;
const CAMERA_EASING: string = SITE_TUNING.relicRun.playerMotion.cameraPanEasing;
const HOP_EASING: string = SITE_TUNING.relicRun.playerMotion.hopArcEasing;
const CAMERA_MOVE_MS = SITE_TUNING.relicRun.playerMotion.cameraPanDurationMs;
const HOP_DURATION_MS = SITE_TUNING.relicRun.playerMotion.hopArcDurationMs;
const BUFFERED_MOVE_LEAD_IN_MS = SITE_TUNING.relicRun.playerMotion.bufferedMoveLeadInMs;
const FALL_DESTINATION_Y_OFFSET = SITE_TUNING.relicRun.portalTransition.fallDestinationYOffsetPx;
const FALL_DIRECTION_RANDOMNESS_RADIANS = SITE_TUNING.relicRun.wrongTile.fallDirectionRandomnessRadians;
const FALL_TILE_DIRECTION_OFFSET_RADIANS = SITE_TUNING.relicRun.wrongTile.sideFallAngleOffsetRadians;
const FALL_SETTLE_MAX_MS = SITE_TUNING.relicRun.wrongTile.settleMaxMs;
const FALL_SETTLE_WITHOUT_HOP_MS = SITE_TUNING.relicRun.wrongTile.settleWithoutHopMs;
const RESPAWN_DURATION_MS = SITE_TUNING.relicRun.portalTransition.respawnDurationMs;
const ENTRY_FALL_DURATION_MS = SITE_TUNING.relicRun.portalTransition.entryFallDurationMs;
const PORTAL_FALL_DURATION_MS = SITE_TUNING.relicRun.portalTransition.portalFallDurationMs;
const BLACKOUT_FADE_IN_MS = SITE_TUNING.relicRun.portalTransition.blackoutFadeInMs;
const OPTIMISTIC_MOVE_TIMEOUT_MS = SITE_TUNING.relicRun.playerMotion.optimisticSyncTimeoutMs;
const DUPLICATE_MOVE_WINDOW_MS = 280;
const TOUCH_SWIPE_MIN_DISTANCE_PX = SITE_TUNING.relicRun.playerMotion.touchSwipeMinDistancePx;
const TOUCH_SWIPE_AXIS_DOMINANCE_RATIO = SITE_TUNING.relicRun.playerMotion.touchSwipeAxisDominanceRatio;
const TOUCH_SWIPE_CLICK_SUPPRESS_MS = SITE_TUNING.relicRun.playerMotion.touchSwipeClickSuppressMs;
const DETAILED_MOVE_FRAME_STALL_LOG_MS = 18;
const MAZE_LONG_FRAME_LOG_MS = 50;
const MAZE_DEBUG_PREFIX = "[Ziarcadia][Maze]";
const MAZE_PERF_PREFIX = "[Ziarcadia][MazePerf]";
const LEVEL_TINTS = SITE_TUNING.relicRun.theme.levelTints;
const LEVEL_STAGE_WASH = String(SITE_TUNING.relicRun.theme.stageWashAlpha);
const LEVEL_STAGE_GRID = String(SITE_TUNING.relicRun.theme.stageGridAlpha);
const LEVEL_TILE_WASH = String(SITE_TUNING.relicRun.theme.tileWashAlpha);
const HOST_SPECTATOR_ROTATE_MS = 5_000;
const PASSAGE_PREVIEW_INITIAL_VISIBLE_TOKENS = SITE_TUNING.relicRun.passagePreview.initialVisibleTokenCount;
const PASSAGE_PREVIEW_CONTEXT_BEFORE_TOKENS = SITE_TUNING.relicRun.passagePreview.replayContextBeforeTokens;
const PASSAGE_PREVIEW_CONTEXT_AFTER_TOKENS = SITE_TUNING.relicRun.passagePreview.replayContextAfterTokens;

const escapeHtml = (value: string): string =>
    value
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#39;");

const samePosition = (left: GridPosition, right: GridPosition): boolean =>
    left.x === right.x && left.y === right.y;

const manhattanDistance = (left: GridPosition, right: GridPosition): number =>
    Math.abs(left.x - right.x) + Math.abs(left.y - right.y);

const positionKey = (position: GridPosition): string => `${position.x},${position.y}`;

const directionalMoveForKey = (key: string): { delta: GridPosition; source: string } | null => {
    switch (key.toLowerCase()) {
        case "arrowup":
        case "w":
            return { delta: { x: 0, y: -1 }, source: "keyboard-up" };
        case "arrowdown":
        case "s":
            return { delta: { x: 0, y: 1 }, source: "keyboard-down" };
        case "arrowleft":
        case "a":
            return { delta: { x: -1, y: 0 }, source: "keyboard-left" };
        case "arrowright":
        case "d":
            return { delta: { x: 1, y: 0 }, source: "keyboard-right" };
        default:
            return null;
    }
};

const directionalMoveForSwipe = (deltaX: number, deltaY: number): { delta: GridPosition; source: string } | null => {
    const absoluteX = Math.abs(deltaX);
    const absoluteY = Math.abs(deltaY);
    const dominantDistance = Math.max(absoluteX, absoluteY);
    const crossDistance = Math.min(absoluteX, absoluteY);

    if (dominantDistance < TOUCH_SWIPE_MIN_DISTANCE_PX) {
        return null;
    }

    if (crossDistance > 0 && dominantDistance / crossDistance < TOUCH_SWIPE_AXIS_DOMINANCE_RATIO) {
        return null;
    }

    if (absoluteX >= absoluteY) {
        return deltaX < 0
            ? { delta: { x: -1, y: 0 }, source: "swipe-left" }
            : { delta: { x: 1, y: 0 }, source: "swipe-right" };
    }

    return deltaY < 0
        ? { delta: { x: 0, y: -1 }, source: "swipe-up" }
        : { delta: { x: 0, y: 1 }, source: "swipe-down" };
};

const isDirectionalKeyControlTarget = (target: EventTarget | null): boolean => {
    if (!(target instanceof Element)) {
        return false;
    }

    if (target.closest(".maze-tile")) {
        return false;
    }

    return Boolean(
        target.closest(
            "button, a, input, textarea, select, [contenteditable='true'], [role='button'], [role='menuitem']"
        )
    );
};

const clonePosition = (position: GridPosition): GridPosition => ({
    x: position.x,
    y: position.y
});

const clonePlayerView = (player: PlayerMazeView): PlayerMazeView => ({
    ...player,
    position: clonePosition(player.position),
    visibleTiles: player.visibleTiles.map((tile) => ({ ...tile })),
    lastEvent: player.lastEvent ? { ...player.lastEvent } : null
});

const compactPassageSnippet = (value: string): string => value.replace(/\s+/g, " ").trim();

const formatRunTime = (elapsedMs: number): string => {
    const safeMs = Math.max(0, Math.round(elapsedMs));
    const minutes = Math.floor(safeMs / 60_000);
    const seconds = (safeMs % 60_000) / 1000;

    if (minutes > 0) {
        return `${minutes}:${seconds.toFixed(2).padStart(5, "0")}`;
    }

    return `${(safeMs / 1000).toFixed(2)}s`;
};

const getLiveRunElapsedMs = (player: PlayerMazeView | null, serverOffsetMs = 0): number => {
    if (!player) {
        return 0;
    }

    const activeElapsedMs =
        typeof player.runTimerStartedAt === "number" && Number.isFinite(player.runTimerStartedAt)
            ? Math.max(0, Date.now() + serverOffsetMs - player.runTimerStartedAt)
            : 0;

    return Math.max(0, player.runElapsedMs + activeElapsedMs);
};

const formatImprovementDuration = (elapsedMs: number): string => {
    const safeMs = Math.max(1, Math.round(elapsedMs));

    if (safeMs < 1000) {
        return `${safeMs} ${safeMs === 1 ? "millisecond" : "milliseconds"}`;
    }

    const seconds = safeMs / 1000;
    const roundedSeconds = seconds < 10 ? seconds.toFixed(2) : seconds.toFixed(1);
    const normalizedSeconds = roundedSeconds.replace(/\.0+$/, "").replace(/(\.\d*[1-9])0+$/, "$1");

    return `${normalizedSeconds} ${normalizedSeconds === "1" ? "second" : "seconds"}`;
};

const buildLevelBestMessage = (improvementMs: number | null): string =>
    improvementMs !== null
        ? `New level personal best! ${formatImprovementDuration(improvementMs)} faster!`
        : "";

const buildPassageTokenSlices = (passageText: string): { normalizedPassage: string; slices: PassageTokenSlice[] } => {
    const normalizedPassage = normalizeSourceText(passageText);

    if (!normalizedPassage) {
        return {
            normalizedPassage: "",
            slices: []
        };
    }

    const tokens = tokenizeSourceText(normalizedPassage);
    let searchFrom = 0;
    const slices: PassageTokenSlice[] = [];

    for (const token of tokens) {
        const start = normalizedPassage.indexOf(token.display, searchFrom);

        if (start < 0) {
            continue;
        }

        const end = start + token.display.length;
        slices.push({
            start,
            end
        });
        searchFrom = end;
    }

    return {
        normalizedPassage,
        slices
    };
};

const buildPassagePreview = (
    cache: PassagePreviewCache,
    targetWordIndex: number
): PassagePreview => {
    const cachePreview = (preview: PassagePreview): PassagePreview => {
        cache.previewsByWordIndex.set(targetWordIndex, preview);
        return preview;
    };
    const cachedPreview = cache.previewsByWordIndex.get(targetWordIndex);

    if (cachedPreview) {
        return cachedPreview;
    }

    const { normalizedPassage, slices } = cache;

    if (!normalizedPassage) {
        return cachePreview({
            text: "",
            truncated: false
        });
    }

    if (slices.length === 0) {
        return cachePreview({
            text: compactPassageSnippet(normalizedPassage),
            truncated: false
        });
    }

    const clampedTargetWordIndex = Math.max(0, Math.min(targetWordIndex, slices.length - 1));

    if (clampedTargetWordIndex <= 0) {
        const endIndex = Math.min(slices.length - 1, PASSAGE_PREVIEW_INITIAL_VISIBLE_TOKENS - 1);

        if (endIndex >= slices.length - 1) {
            return cachePreview({
                text: compactPassageSnippet(normalizedPassage),
                truncated: false
            });
        }

        const leadingSnippet = compactPassageSnippet(normalizedPassage.slice(0, slices[endIndex].end));
        return cachePreview({
            text: `${leadingSnippet} ...`,
            truncated: true
        });
    }

    const desiredWindowSize =
        PASSAGE_PREVIEW_CONTEXT_BEFORE_TOKENS + 1 + PASSAGE_PREVIEW_CONTEXT_AFTER_TOKENS;
    let startIndex = Math.max(0, clampedTargetWordIndex - PASSAGE_PREVIEW_CONTEXT_BEFORE_TOKENS);
    let endIndex = Math.min(slices.length - 1, clampedTargetWordIndex + PASSAGE_PREVIEW_CONTEXT_AFTER_TOKENS);

    while (endIndex - startIndex + 1 < desiredWindowSize && startIndex > 0) {
        startIndex -= 1;
    }

    while (endIndex - startIndex + 1 < desiredWindowSize && endIndex < slices.length - 1) {
        endIndex += 1;
    }

    const middleSnippet = compactPassageSnippet(
        normalizedPassage.slice(slices[startIndex].start, slices[endIndex].end)
    );
    const leadingEllipsis = startIndex > 0 ? "... " : "";
    const trailingEllipsis = endIndex < slices.length - 1 ? " ..." : "";

    const preview = {
        text: `${leadingEllipsis}${middleSnippet}${trailingEllipsis}`,
        truncated: Boolean(leadingEllipsis || trailingEllipsis)
    };

    return cachePreview(preview);
};

const createPassagePreviewCache = (passageText: string): PassagePreviewCache => {
    const { normalizedPassage, slices } = buildPassageTokenSlices(passageText);

    return {
        sourceText: passageText,
        normalizedPassage,
        slices,
        previewsByWordIndex: new Map()
    };
};

const logMazeDebug = (message: string, details?: Record<string, unknown>) => {
    if (!isMazeDebugEnabled()) {
        return;
    }

    appendCodexDebugLog("relic-run", "info", message, details);
    if (details) {
        console.info(MAZE_DEBUG_PREFIX, message, details);
        return;
    }

    console.info(MAZE_DEBUG_PREFIX, message);
};

const roundPerfMs = (value: number): number => Number(value.toFixed(2));

const formatPerfPhases = (phases: Record<string, number>): Record<string, number> => {
    const formatted: Record<string, number> = {};

    for (const [phase, value] of Object.entries(phases)) {
        if (value > 0.01) {
            formatted[phase] = roundPerfMs(value);
        }
    }

    return formatted;
};

const formatPerfPhaseSummary = (phases: Record<string, number>): string =>
    Object.entries(formatPerfPhases(phases))
        .sort((left, right) => right[1] - left[1])
        .slice(0, 4)
        .map(([phase, value]) => `${phase}:${value}ms`)
        .join(", ");

const logMazePerf = (message: string, details?: Record<string, unknown>) => {
    if (!isMazePerfEnabled()) {
        return;
    }

    if (details) {
        console.info(MAZE_PERF_PREFIX, message, details);
        return;
    }

    console.info(MAZE_PERF_PREFIX, message);
};

const logMazePerfDiagnostic = (message: string, details?: Record<string, unknown>) => {
    if (!isMazePerfDiagnosticsEnabled()) {
        return;
    }

    if (details) {
        console.warn(MAZE_PERF_PREFIX, message, details);
        return;
    }

    console.warn(MAZE_PERF_PREFIX, message);
};

const reportMazePerfLoggingError = (() => {
    let logged = false;

    return (error: unknown) => {
        if (logged) {
            return;
        }

        logged = true;
        console.warn(MAZE_PERF_PREFIX, "logging-error", error);
    };
})();

const isMazeDebugEnabled = (() => {
    let cached: boolean | null = null;

    return (): boolean => {
        if (cached !== null) {
            return cached;
        }

        try {
            const params = new URLSearchParams(window.location.search);
            cached =
                params.has("mazeDebug") ||
                params.has("debug") ||
                window.localStorage.getItem("ziarcadia:maze-debug") === "1" ||
                window.localStorage.getItem("ziarcadia:debug") === "1";
        } catch {
            cached = false;
        }

        return cached;
    };
})();

type MazePerfLevel = "off" | "diagnostics" | "detailed";

const getMazePerfLevel = (() => {
    let cached: MazePerfLevel | null = null;

    return (): MazePerfLevel => {
        if (cached !== null) {
            return cached;
        }

        try {
            const params = new URLSearchParams(window.location.search);
            const localOverride = window.localStorage.getItem("ziarcadia:maze-perf");
            const optedOut =
                localOverride === "0" || params.has("mazePerfOff") || params.has("perfOff");
            const detailed =
                params.has("mazePerf") ||
                params.has("perf") ||
                params.has("mazeDebug") ||
                params.has("debug") ||
                localOverride === "1" ||
                window.localStorage.getItem("ziarcadia:maze-debug") === "1" ||
                window.localStorage.getItem("ziarcadia:debug") === "1";
            cached = optedOut ? "off" : detailed ? "detailed" : "diagnostics";
        } catch {
            cached = "diagnostics";
        }

        return cached;
    };
})();

const isMazePerfEnabled = (): boolean => getMazePerfLevel() === "detailed";
const isMazePerfDiagnosticsEnabled = (): boolean => getMazePerfLevel() !== "off";

const buildRosterActionsKey = (sessionId: string): string => `relic:${sessionId}`;

const participantMarkup = (
    participants: RoomParticipantView[],
    highlightedSessionId: string | null = null,
    options: {
        allowHostPromptRename?: boolean;
        allowHostKick?: boolean;
        openActionsKey?: string | null;
    } = {}
): string => {
    if (participants.length === 0) {
        return `<p class="roster-empty">${escapeHtml(t("noStudentsYet"))}</p>`;
    }

    return `
        <ul class="${getPlayerRosterListClass("roster-list", participants.length)}">
            ${participants
                .map(
                    (participant) => {
                        const actionsKey = buildRosterActionsKey(participant.sessionId);
                        const displayName = getPlayerRosterDisplayName(
                            participant.name,
                            participant.renamePending,
                            t("renamePendingName")
                        );
                        const hasBestTime =
                            typeof participant.relicRunBestMs === "number" && Number.isFinite(participant.relicRunBestMs);
                        const bestTimeMarkup = hasBestTime
                            ? `<span class="roster-meta">${escapeHtml(formatRunTime(participant.relicRunBestMs ?? 0))}</span>`
                            : `<span class="roster-meta roster-meta-running" title="${escapeHtml(
                                  t("treasureHunting")
                              )}" aria-label="${escapeHtml(t("treasureHunting"))}">
                                    <span class="roster-progress-arrow" aria-hidden="true"></span>
                                    <span class="roster-progress-relic" aria-hidden="true"></span>
                                </span>`;
                        const actionMenu = renderPlayerRosterActionsMenu(
                            `${t("playerActions")}: ${displayName}`,
                            [
                                ...(options.allowHostPromptRename
                                    ? [
                                          {
                                              attributes: {
                                                  "data-action": "prompt-rename-roster-participant",
                                                  "data-session-id": participant.sessionId,
                                                  "aria-label": `${t("askRename")}: ${displayName}`,
                                                  title: `${t("askRename")}: ${displayName}`
                                              },
                                              className: "participant-rename-prompt-button",
                                              label: t("askRename")
                                          }
                                      ]
                                    : []),
                                ...(options.allowHostKick
                                    ? [
                                          {
                                              attributes: {
                                                  "data-action": "kick-roster-participant",
                                                  "data-session-id": participant.sessionId,
                                                  "aria-label": `${t("kick")}: ${displayName}`,
                                                  title: `${t("kick")}: ${displayName}`
                                              },
                                              className: "participant-kick-button",
                                              label: t("kick")
                                          }
                                      ]
                                    : [])
                            ],
                            {
                                key: actionsKey,
                                open: options.openActionsKey === actionsKey,
                                scope: "relic-run",
                                triggerClassName: "roster-name",
                                triggerLabel: displayName
                            }
                        );

                        return `
                        <li class="roster-row${
                            highlightedSessionId && participant.sessionId === highlightedSessionId
                                ? " is-active-view"
                                : ""
                        }${getPlayerRosterPlacementClass(participant.placement)}${getPlayerRenamePendingClass(
                            participant.renamePending
                        )}">
                            <div class="participant-identity">
                                ${actionMenu || `<span class="roster-name">${escapeHtml(displayName)}</span>`}
                            </div>
                            ${bestTimeMarkup}
                        </li>
                    `;
                    }
                )
                .join("")}
        </ul>
    `;
};

export class MazeView {
    private readonly root: HTMLElement;
    private readonly layout: HTMLElement;
    private readonly onMove: (target: GridPosition) => void;
    private readonly onReady: () => void;
    private readonly onReplay: () => void;
    private readonly onPromptRenameParticipant: ((sessionId: string) => void) | null;
    private readonly onKickParticipant: ((sessionId: string) => void) | null;
    private readonly boardShell: HTMLElement;
    private readonly board: HTMLElement;
    private readonly boardCanvas: HTMLCanvasElement;
    private readonly boardRenderer: MazeCanvasRenderer;
    private readonly stage: HTMLElement;
    private readonly avatarLayer: HTMLElement;
    private readonly avatar: HTMLElement;
    private readonly overlay: HTMLElement;
    private readonly jumpTrail: HTMLElement;
    private readonly flare: HTMLElement;
    private readonly pit: HTMLElement;
    private readonly fallCurtain: HTMLElement;
    private readonly levelBestCallout: HTMLElement;
    private readonly passageButton: HTMLButtonElement;
    private readonly passageInline: HTMLElement;
    private readonly timerChip: HTMLElement;
    private readonly viewerBadge: HTMLElement;
    private readonly levelLabel: HTMLElement;
    private readonly progressBar: HTMLElement;
    private readonly progressFill: HTMLElement;
    private readonly rosterSection: HTMLElement;
    private readonly rosterLabel: HTMLElement;
    private readonly rosterHistorySlot: HTMLElement;
    private readonly rosterBody: HTMLElement;
    private readonly handleWindowResize: () => void;
    private readonly handleWindowKeyDown: (event: KeyboardEvent) => void;
    private readonly resizeObserver: ResizeObserver | null;
    private readonly tiles = new Map<string, HTMLButtonElement>();
    private readonly collapsedTileKeys = new Set<string>();
    private readonly animationTimers = new Set<number>();
    private pressedTile: HTMLButtonElement | null = null;
    private rosterSignature = "";
    private openRosterActionsKey: string | null = null;
    private playerHistoryMenuOpen = false;
    private pendingMovePerf: MazeMovePerfSample | null = null;
    private activeMovePerf: MazeMovePerfSample | null = null;
    private nextMovePerfId = 0;
    private jumpTrailAnimation: Animation | null = null;
    private jumpTrailStartTimer: number | null = null;
    private avatarVisibilityGuardFrame: number | null = null;
    private avatarVisibilityGuardTimer: number | null = null;
    private timerRefreshTimer: number | null = null;
    private rosterLayoutFrameId: number | null = null;
    private stageWidth = 0;
    private stageHeight = 0;
    private boardScale = 1;
    private boardInputEnabled = false;
    private serverOffsetMs = 0;
    private visibleTiles: MazeTileView[] = [];
    private currentPlayer: PlayerMazeView | null = null;
    private currentRoom: RoomView | null = null;
    private pendingRoom: RoomView | null = null;
    private spectatorPlayerSessionId: string | null = null;
    private spectatorRotationTimer: number | null = null;
    private spectatorRotateAt = 0;
    private visualCenter: GridPosition | null = null;
    private avatarPosition: GridPosition | null = null;
    private entryCorner: "top-left" | "bottom-left" = "top-left";
    private courseBounds: RelicCourseBounds | undefined;
    private coursePlacement: { x: number; y: number } | undefined;
    private optimisticMove:
        | {
              from: GridPosition;
              target: GridPosition;
              startedAt: number;
          }
        | null = null;
    private optimisticMoveTimer: number | null = null;
    private avatarAnimation: Animation | null = null;
    private avatarAnimationNonce = 0;
    private lastHandledResetId = 0;
    private isResetAnimating = false;
    private lastHandledAdvanceId = 0;
    private isAdvanceAnimating = false;
    private isReplayPending = false;
    private lastAttemptedMove: GridPosition | null = null;
    private lastDispatchedMoveKey = "";
    private lastDispatchedMoveAt = 0;
    private bufferedMove: BufferedMoveIntent | null = null;
    private bufferedMoveQueue: BufferedMoveIntent[] = [];
    private touchSwipe: TouchSwipeIntent | null = null;
    private suppressPointerUntilRelease = false;
    private suppressTouchClickUntil = 0;
    private passageText = "";
    private passagePreviewCache: PassagePreviewCache | null = null;
    private isPassageVisible = false;
    private isEntryAnimating = false;
    private appliedLevelTintSignature = "";
    private lastReadySignature = "";
    private lastReadySentAt = 0;

    constructor(root: HTMLElement, options: MazeViewOptions) {
        applySiteTuningCssVariables();
        this.root = root;
        this.onMove = options.onMove;
        this.onReady = options.onReady;
        this.onReplay = options.onReplay;
        this.onPromptRenameParticipant = options.onPromptRenameParticipant ?? null;
        this.onKickParticipant = options.onKickParticipant ?? null;
        this.root.innerHTML = `
            <div class="active-layout relic-run-layout">
                <article class="maze-stage-card">
                    <div class="maze-progress-panel">
                        <p class="maze-time-chip" aria-live="off"></p>
                        <div
                            class="maze-progress-bar"
                            role="progressbar"
                            aria-label="${escapeHtml(t("progress"))}"
                            aria-valuemin="0"
                            aria-valuemax="100"
                            aria-valuenow="0"
                        >
                            <div class="maze-progress-fill"></div>
                        </div>
                    </div>
                    <div class="maze-stage">
                        <button
                            id="maze-passage-button"
                            class="secondary-button maze-passage-button"
                            type="button"
                        >
                            ${escapeHtml(t("showTextAgain"))}
                        </button>
                        <div class="maze-passage-inline" hidden aria-live="polite"></div>
                        <p class="maze-viewer-badge"></p>
                        <p class="maze-level-chip"></p>
                        <div class="maze-board-shell">
                            <canvas class="maze-board-canvas" aria-hidden="true"></canvas>
                            <div class="maze-board"></div>
                        </div>
                        <div class="maze-jump-trail"></div>
                        <div class="maze-flare"></div>
                        <div class="maze-pit"></div>
                        <div class="maze-avatar-layer">
                            <div class="maze-avatar"></div>
                        </div>
                        <div class="maze-fall-curtain" hidden></div>
                        <div class="maze-level-best-callout" hidden></div>
                        <div class="maze-overlay" hidden></div>
                    </div>
                </article>
                <aside class="active-side-card">
                    <section class="roster-card">
                        <div class="roster-head">
                            <p class="card-label maze-roster-label" hidden>${escapeHtml(t("roomRoster"))}</p>
                            <span class="roster-history-slot" data-roster-history-slot></span>
                        </div>
                        <div class="roster-body"></div>
                    </section>
                    <section class="card active-host-utility-card" data-active-host-utility-root hidden></section>
                </aside>
            </div>
        `;

        this.layout = this.root.querySelector<HTMLElement>(".active-layout")!;
        this.boardShell = this.root.querySelector<HTMLElement>(".maze-board-shell")!;
        this.board = this.root.querySelector<HTMLElement>(".maze-board")!;
        this.boardCanvas = this.root.querySelector<HTMLCanvasElement>(".maze-board-canvas")!;
        const detailedPerfLogging = isMazePerfEnabled();
        this.boardRenderer = new MazeCanvasRenderer(this.boardCanvas, {
            onPerfSample:
                detailedPerfLogging || isMazePerfDiagnosticsEnabled()
                    ? (sample) => this.logCanvasPerf(sample)
                    : null,
            perfThresholdMs: detailedPerfLogging ? DETAILED_MOVE_FRAME_STALL_LOG_MS : MAZE_LONG_FRAME_LOG_MS
        });
        this.stage = this.root.querySelector<HTMLElement>(".maze-stage")!;
        this.stage.style.setProperty("--maze-reveal-radius", String(MAZE_REVEAL_RADIUS));
        this.avatarLayer = this.root.querySelector<HTMLElement>(".maze-avatar-layer")!;
        this.avatar = this.root.querySelector<HTMLElement>(".maze-avatar")!;
        this.avatar.append(createCapyShieldElement({ className: "maze-capy" }));
        this.overlay = this.root.querySelector<HTMLElement>(".maze-overlay")!;
        this.jumpTrail = this.root.querySelector<HTMLElement>(".maze-jump-trail")!;
        this.flare = this.root.querySelector<HTMLElement>(".maze-flare")!;
        this.pit = this.root.querySelector<HTMLElement>(".maze-pit")!;
        this.fallCurtain = this.root.querySelector<HTMLElement>(".maze-fall-curtain")!;
        this.levelBestCallout = this.root.querySelector<HTMLElement>(".maze-level-best-callout")!;
        this.passageButton = this.root.querySelector<HTMLButtonElement>("#maze-passage-button")!;
        this.passageInline = this.root.querySelector<HTMLElement>(".maze-passage-inline")!;
        this.timerChip = this.root.querySelector<HTMLElement>(".maze-time-chip")!;
        this.viewerBadge = this.root.querySelector<HTMLElement>(".maze-viewer-badge")!;
        this.levelLabel = this.root.querySelector<HTMLElement>(".maze-level-chip")!;
        this.progressBar = this.root.querySelector<HTMLElement>(".maze-progress-bar")!;
        this.progressFill = this.root.querySelector<HTMLElement>(".maze-progress-fill")!;
        this.rosterSection = this.root.querySelector<HTMLElement>(".roster-card")!;
        this.rosterLabel = this.root.querySelector<HTMLElement>(".maze-roster-label")!;
        this.rosterHistorySlot = this.root.querySelector<HTMLElement>("[data-roster-history-slot]")!;
        this.rosterBody = this.root.querySelector<HTMLElement>(".roster-body")!;
        this.stage.classList.add("is-canvas-rendered");
        this.refreshStageMetrics();
        this.handleWindowResize = () => {
            this.refreshStageMetrics();
            this.recenterBoardAfterResize();
            this.scheduleRosterLayoutSync();
        };
        this.handleWindowKeyDown = (event) => {
            this.handleDirectionalKeyDown(event);
        };
        this.resizeObserver =
            typeof ResizeObserver === "undefined"
                ? null
                : new ResizeObserver(() => {
                      this.handleWindowResize();
                  });
        this.resizeObserver?.observe(this.stage);
        window.addEventListener("resize", this.handleWindowResize);
        window.addEventListener("keydown", this.handleWindowKeyDown);
        this.stage.addEventListener(
            "pointerdown",
            (event) => {
                this.handleTouchSwipePointerDown(event);
            },
            { capture: true }
        );
        this.stage.addEventListener(
            "pointermove",
            (event) => {
                this.handleTouchSwipePointerMove(event);
            },
            { capture: true }
        );
        this.stage.addEventListener(
            "pointerup",
            (event) => {
                this.handleTouchSwipePointerUp(event);
            },
            { capture: true }
        );
        this.stage.addEventListener(
            "pointercancel",
            (event) => {
                this.handleTouchSwipePointerCancel(event);
            },
            { capture: true }
        );
        this.stage.addEventListener(
            "pointerdown",
            (event) => {
                this.handleStagePointer(event, "stage-pointerdown");
            },
            { capture: true }
        );
        this.stage.addEventListener(
            "click",
            (event) => {
                this.handleStagePointer(event, "stage-click");
            },
            { capture: true }
        );
        this.board.addEventListener("pointerdown", (event) => {
            const tile = this.findTileButton(event.target);

            if (!tile) {
                return;
            }

            if (!this.currentPlayer?.isViewerControlled) {
                return;
            }

            this.logTileEvent("pointerdown", tile);

            if (tile.dataset.blocked === "true") {
                return;
            }

            this.setPressedTile(tile);
            this.tryMove(tile, "pointerdown");
        });
        this.board.addEventListener("click", (event) => {
            if (this.consumeSuppressedTouchClick(event)) {
                return;
            }

            const tile = this.findTileButton(event.target);

            if (!tile) {
                return;
            }

            if (!this.currentPlayer?.isViewerControlled) {
                return;
            }

            event.preventDefault();
            this.logTileEvent("click", tile);
            this.tryMove(tile, "click");
        });
        this.board.addEventListener("pointerup", (event) => {
            const tile = this.findTileButton(event.target);

            if (!tile) {
                return;
            }

            this.clearPressedTiles();
        });
        this.board.addEventListener("pointercancel", () => {
            this.clearPressedTiles();
        });
        this.board.addEventListener("pointerleave", () => {
            this.clearPressedTiles();
        });
        this.root.addEventListener("click", (event) => {
            const target = event.target as HTMLElement | null;
            const passageButtonCandidate = target?.closest("#maze-passage-button");
            const passageButton =
                passageButtonCandidate instanceof HTMLButtonElement
                    ? passageButtonCandidate
                    : null;
            const replayButtonCandidate = target?.closest("#maze-replay-button");
            const replayButton =
                replayButtonCandidate instanceof HTMLButtonElement
                    ? replayButtonCandidate
                    : null;
            const kickParticipantButton = target?.closest<HTMLElement>(
                '[data-action="kick-roster-participant"]'
            );
            const promptRenameParticipantButton = target?.closest<HTMLElement>(
                '[data-action="prompt-rename-roster-participant"]'
            );
            const playerHistoryMenu = target?.closest<HTMLElement>("[data-player-history-menu]");
            const playerHistoryMenuToggle = target?.closest<HTMLElement>("[data-player-history-menu-toggle]");
            const playerHistoryDownloadButton = target?.closest<HTMLElement>(
                "[data-player-history-download]"
            );
            const rosterActionsToggle = target?.closest<HTMLElement>(
                '[data-action="toggle-roster-actions"]'
            );

            if (playerHistoryMenuToggle) {
                event.preventDefault();
                event.stopPropagation();
                this.playerHistoryMenuOpen = !this.playerHistoryMenuOpen;
                this.openRosterActionsKey = null;
                this.rosterSignature = "";
                if (this.currentRoom) {
                    this.applyRoom(this.currentRoom, this.resolveDisplayedPlayer(this.currentRoom));
                }
                return;
            }

            if (playerHistoryDownloadButton) {
                event.preventDefault();
                event.stopPropagation();
                this.playerHistoryMenuOpen = false;
                if (this.currentRoom) {
                    downloadPlayerHistory(this.currentRoom);
                    this.applyRoom(this.currentRoom, this.resolveDisplayedPlayer(this.currentRoom));
                }
                return;
            }

            if (this.playerHistoryMenuOpen && !playerHistoryMenu) {
                this.playerHistoryMenuOpen = false;
                this.rosterSignature = "";
                if (this.currentRoom) {
                    this.applyRoom(this.currentRoom, this.resolveDisplayedPlayer(this.currentRoom));
                }
            }

            if (rosterActionsToggle) {
                event.preventDefault();
                event.stopPropagation();
                const actionsKey = rosterActionsToggle.getAttribute("data-roster-action-key") ?? "";
                this.openRosterActionsKey = actionsKey && this.openRosterActionsKey !== actionsKey ? actionsKey : null;
                this.playerHistoryMenuOpen = false;
                this.rosterSignature = "";
                if (this.currentRoom) {
                    this.applyRoom(this.currentRoom, this.resolveDisplayedPlayer(this.currentRoom));
                }
                return;
            }

            if (promptRenameParticipantButton && this.onPromptRenameParticipant) {
                event.preventDefault();
                const sessionId = promptRenameParticipantButton.getAttribute("data-session-id") ?? "";
                this.openRosterActionsKey = null;
                this.rosterSignature = "";
                if (this.currentRoom) {
                    this.applyRoom(this.currentRoom, this.resolveDisplayedPlayer(this.currentRoom));
                }

                if (sessionId) {
                    this.onPromptRenameParticipant(sessionId);
                }
                return;
            }

            if (kickParticipantButton && this.onKickParticipant) {
                event.preventDefault();
                const sessionId = kickParticipantButton.getAttribute("data-session-id") ?? "";
                this.openRosterActionsKey = null;
                this.rosterSignature = "";
                if (this.currentRoom) {
                    this.applyRoom(this.currentRoom, this.resolveDisplayedPlayer(this.currentRoom));
                }

                if (sessionId) {
                    this.onKickParticipant(sessionId);
                }
                return;
            }

            if (
                passageButton &&
                this.passageText &&
                !this.isPassageVisible &&
                !this.currentPlayer?.completed &&
                !this.isResetAnimating &&
                !this.isAdvanceAnimating &&
                !this.isEntryAnimating &&
                !this.optimisticMove
            ) {
                this.showPassageText();
                return;
            }

            if (
                !replayButton ||
                !this.currentPlayer?.completed ||
                !this.currentPlayer.canReplay ||
                !this.currentPlayer.isViewerControlled ||
                this.isReplayPending
            ) {
                return;
            }

            this.isReplayPending = true;
            replayButton.disabled = true;
            this.onReplay();
        });
    }

    recordAttempt(target: GridPosition) {
        this.lastAttemptedMove = clonePosition(target);
    }

    private createMovePerfSample(source: string, tileKey: string, target: GridPosition): MazeMovePerfSample {
        return {
            id: ++this.nextMovePerfId,
            source,
            tileKey,
            target: clonePosition(target),
            startedAt: performance.now(),
            phases: {},
            loggedAwaitingConfirmation: false
        };
    }

    private measureActiveMovePhase<T>(phase: string, work: () => T): T {
        const activeMovePerf = this.activeMovePerf;

        if (!activeMovePerf) {
            return work();
        }

        const startedAt = performance.now();
        const result = work();
        activeMovePerf.phases[phase] = (activeMovePerf.phases[phase] ?? 0) + (performance.now() - startedAt);
        return result;
    }

    private getActivePerfEffects(): string[] {
        const effects: string[] = [];

        if (this.jumpTrailStartTimer !== null) {
            effects.push("trail-pending");
        }

        if (this.jumpTrailAnimation !== null) {
            effects.push("trail");
        }

        if (this.flare.classList.contains("is-active")) {
            effects.push("flare");
        }

        if (this.pit.classList.contains("is-active")) {
            effects.push("pit");
        }

        if (!this.overlay.hidden) {
            effects.push("overlay");
        }

        const fallCurtainOpacity = roundPerfMs(this.getFallCurtainOpacity());

        if (fallCurtainOpacity > 0.02) {
            effects.push(`curtain:${fallCurtainOpacity}`);
        }

        if (this.isResetAnimating) {
            effects.push("reset");
        }

        if (this.isAdvanceAnimating) {
            effects.push("advance");
        }

        if (this.isEntryAnimating) {
            effects.push("entry");
        }

        if (this.bufferedMove) {
            effects.push("buffered");
        }

        return effects;
    }

    private buildMovePerfContextDetails(): Record<string, unknown> {
        return {
            activeEffects: this.getActivePerfEffects(),
            boardScale: roundPerfMs(this.getBoardScale()),
            bufferedMove: this.bufferedMove
                ? {
                      queuedAtMs: roundPerfMs(performance.now() - this.bufferedMove.queuedAt),
                      target: this.bufferedMove.target,
                      tileKey: this.bufferedMove.tileKey
                  }
                : null,
            stageHeight: this.stageHeight,
            stageWidth: this.stageWidth,
            tileButtons: this.tiles.size,
            visibleTiles: this.visibleTiles.length
        };
    }

    private formatMovePerfContextSummary(): string {
        const effects = this.getActivePerfEffects();
        const effectSummary = effects.length > 0 ? ` fx=${effects.join("+")}` : "";
        return ` board=${this.visibleTiles.length}t stage=${this.stageWidth}x${this.stageHeight} scale=${roundPerfMs(this.getBoardScale())}${effectSummary}`;
    }

    private formatCanvasPerfInline(sample: MazeCanvasFramePerfSample | null): string {
        if (!sample) {
            return "renderer=none";
        }

        const phaseSummary = formatPerfPhaseSummary(sample.phases);
        const canvasScale = roundPerfMs(sample.devicePixelRatio);
        const ageMs = roundPerfMs(performance.now() - sample.recordedAt);

        return `renderer=${sample.reason}:${roundPerfMs(sample.totalMs)}ms age=${ageMs}ms canvas=${sample.canvasCssWidth}x${sample.canvasCssHeight}@${canvasScale}x glow=${sample.glowTileCount} trail=${sample.trailTileCount} collapse=${sample.collapseTileCount}${phaseSummary ? ` phases=${phaseSummary}` : ""}`;
    }

    private logCanvasPerf(sample: MazeCanvasFramePerfSample) {
        try {
            const phaseSummary = formatPerfPhaseSummary(sample.phases);
            const boardContext = this.formatMovePerfContextSummary();
            const message =
                `canvas-frame reason=${sample.reason} total=${roundPerfMs(sample.totalMs)}ms tiles=${sample.tileCount} glow=${sample.glowTileCount} trail=${sample.trailTileCount} collapse=${sample.collapseTileCount} canvas=${sample.canvasCssWidth}x${sample.canvasCssHeight}@${roundPerfMs(sample.devicePixelRatio)}x${sample.ambient ? " ambient" : ""}${phaseSummary ? ` phases=${phaseSummary}` : ""}${boardContext}`;
            const details = {
                ...this.buildMovePerfContextDetails(),
                ...sample,
                phases: formatPerfPhases(sample.phases)
            };

            if (sample.totalMs >= MAZE_LONG_FRAME_LOG_MS) {
                logMazePerfDiagnostic(message, details);
            } else {
                logMazePerf(message, details);
            }
        } catch (error) {
            reportMazePerfLoggingError(error);
        }
    }

    private logMovePerf(message: string, movePerf: MazeMovePerfSample, details: Record<string, unknown> = {}) {
        try {
            const totalMs = roundPerfMs(performance.now() - movePerf.startedAt);
            const phaseSummary = formatPerfPhaseSummary(movePerf.phases);
            const contextSummary = this.formatMovePerfContextSummary();
            logMazePerf(
                `${message} moveId=${movePerf.id} total=${totalMs}ms${phaseSummary ? ` phases=${phaseSummary}` : ""}${contextSummary}`,
                {
                    moveId: movePerf.id,
                    source: movePerf.source,
                    tileKey: movePerf.tileKey,
                    target: movePerf.target,
                    totalMs,
                    phases: formatPerfPhases(movePerf.phases),
                    ...this.buildMovePerfContextDetails(),
                    ...details
                }
            );
        } catch (error) {
            reportMazePerfLoggingError(error);
        }
    }

    private scheduleMoveFramePerf(movePerf: MazeMovePerfSample, visibleTileCount: number) {
        if (!isMazePerfDiagnosticsEnabled()) {
            return;
        }

        const stallThresholdMs = isMazePerfEnabled()
            ? DETAILED_MOVE_FRAME_STALL_LOG_MS
            : MAZE_LONG_FRAME_LOG_MS;

        requestAnimationFrame((firstFrameAt) => {
            const firstFrameMs = roundPerfMs(Math.max(0, firstFrameAt - movePerf.startedAt));

            requestAnimationFrame((secondFrameAt) => {
                const secondFrameGapMs = roundPerfMs(secondFrameAt - firstFrameAt);

                if (firstFrameMs < stallThresholdMs && secondFrameGapMs < stallThresholdMs) {
                    return;
                }

                try {
                    const rendererSample = this.boardRenderer.getLastFramePerfSample(true);
                    const rendererSummary = this.formatCanvasPerfInline(rendererSample);
                    const activeEffects = this.getActivePerfEffects();
                    const effectSummary = activeEffects.length > 0 ? ` fx=${activeEffects.join("+")}` : "";

                    const message =
                        `move-frame moveId=${movePerf.id} firstFrame=${firstFrameMs}ms secondFrameGap=${secondFrameGapMs}ms tiles=${visibleTileCount} ${rendererSummary}${effectSummary}`;
                    const details = {
                        activeEffects,
                        boardScale: roundPerfMs(this.getBoardScale()),
                        moveId: movePerf.id,
                        firstFrameMs,
                        renderer: rendererSample
                            ? {
                                  ...rendererSample,
                                  phases: formatPerfPhases(rendererSample.phases)
                              }
                            : null,
                        secondFrameGapMs,
                        stageHeight: this.stageHeight,
                        stageWidth: this.stageWidth,
                        visibleTiles: visibleTileCount
                    };

                    if (firstFrameMs >= MAZE_LONG_FRAME_LOG_MS || secondFrameGapMs >= MAZE_LONG_FRAME_LOG_MS) {
                        logMazePerfDiagnostic(message, details);
                    } else {
                        logMazePerf(message, details);
                    }
                } catch (error) {
                    reportMazePerfLoggingError(error);
                }
            });
        });
    }

    private reportVisibleTileAnomaly(player: PlayerMazeView) {
        if (player.completed || this.isResetAnimating || this.isAdvanceAnimating || this.isEntryAnimating) {
            return;
        }

        const reachableGoalTiles = player.visibleTiles.filter(
            (tile) =>
                tile.isReachable && (tile.kind === "path" || tile.kind === "portal" || tile.kind === "end")
        );

        if (reachableGoalTiles.length === 1) {
            return;
        }

        logMazePerfDiagnostic("visible-tile-anomaly", {
            currentWord: player.currentWord,
            nextWord: player.nextWord,
            position: player.position,
            progressIndex: player.progressIndex,
            reachableGoalTiles: reachableGoalTiles.map((tile) => ({
                key: tile.key,
                kind: tile.kind,
                word: tile.word
            })),
            reachableTiles: player.visibleTiles
                .filter((tile) => tile.isReachable)
                .map((tile) => ({
                    key: tile.key,
                    kind: tile.kind,
                    word: tile.word
                }))
        });
    }

    private finalizePendingMovePerf(message: string, details: Record<string, unknown> = {}) {
        const movePerf = this.pendingMovePerf;
        this.pendingMovePerf = null;
        this.activeMovePerf = null;

        if (movePerf) {
            this.logMovePerf(message, movePerf, details);
        }
    }

    private clearBufferedMove() {
        this.bufferedMove = null;
        this.bufferedMoveQueue = [];
    }

    private getInputPosition(): GridPosition | null {
        return this.bufferedMoveQueue.at(-1)?.target ?? this.bufferedMove?.target ?? this.optimisticMove?.target ?? this.currentPlayer?.position ?? null;
    }

    private queueBufferedMove(element: HTMLButtonElement, source: string): boolean {
        if (!this.optimisticMove) {
            return false;
        }

        const tileKey = element.dataset.tileKey ?? "";
        const x = Number(element.dataset.moveX);
        const y = Number(element.dataset.moveY);

        if (!Number.isFinite(x) || !Number.isFinite(y)) {
            return false;
        }

        const target = { x, y };

        const inputPosition = this.getInputPosition()!;
        if (samePosition(target, inputPosition)) {
            return true;
        }

        const optimisticTile = this.visibleTiles.find((tile) => tile.key === tileKey);
        const isValidBufferedMove =
            optimisticTile &&
            !element.classList.contains("is-fogged") &&
            manhattanDistance(optimisticTile, inputPosition) === 1 &&
            optimisticTile.kind !== "trail" && optimisticTile.kind !== "start";

        if (!isValidBufferedMove) {
            return false;
        }

        const now = performance.now();
        const optimisticAgeMs = now - this.optimisticMove.startedAt;

        // Let deliberate near-landing taps stay responsive while making an early double-click
        // much less likely to queue a second accidental move.
        const isTrace = source.includes("drag");
        if (!isTrace && (optimisticAgeMs < BUFFERED_MOVE_LEAD_IN_MS || this.bufferedMove)) {
            logMazeDebug("buffered move ignored; hop is still too fresh", {
                optimisticAgeMs: roundPerfMs(optimisticAgeMs),
                source,
                target,
                tileKey
            });
            return false;
        }

        if (this.bufferedMove && this.bufferedMove.tileKey === tileKey && now - this.bufferedMove.queuedAt < DUPLICATE_MOVE_WINDOW_MS) {
            return true;
        }

        const intent = {
            queuedAt: now,
            source,
            target,
            tileKey
        };
        if (!this.bufferedMove) {
            this.bufferedMove = intent;
        } else if (this.bufferedMoveQueue.length < SITE_TUNING.relicRun.playerMotion.maxTracedMoveQueue - 1) {
            this.bufferedMoveQueue.push(intent);
        } else {
            return false;
        }
        logMazePerf(`move-buffered tileKey=${tileKey} via=${source}`, {
            currentPosition: this.currentPlayer?.position ?? null,
            optimisticAgeMs: roundPerfMs(optimisticAgeMs),
            pendingMoveId: this.pendingMovePerf?.id ?? null,
            target,
            through: this.optimisticMove.target,
            tileKey
        });
        return true;
    }

    private flushBufferedMoveIfPossible(trigger: string) {
        const bufferedMove = this.bufferedMove;

        if (!bufferedMove || !this.currentPlayer || this.currentPlayer.completed || this.isResetAnimating || this.isAdvanceAnimating || this.optimisticMove) {
            return;
        }

        const tile = this.visibleTiles.find((candidate) => candidate.key === bufferedMove.tileKey);
        const element = this.tiles.get(bufferedMove.tileKey);

        if (
            !tile ||
            !element ||
            element.dataset.blocked === "true" ||
            manhattanDistance(tile, this.currentPlayer.position) !== 1
        ) {
            logMazePerf(`move-buffer-expired trigger=${trigger}`, {
                bufferedTarget: bufferedMove.target,
                currentPosition: this.currentPlayer.position,
                tileKey: bufferedMove.tileKey,
                trigger
            });
            this.clearBufferedMove();
            return;
        }

        this.bufferedMove = this.bufferedMoveQueue.shift() ?? null;
        logMazePerf(`move-buffer-flush trigger=${trigger}`, {
            bufferedTarget: bufferedMove.target,
            currentPosition: this.currentPlayer.position,
            tileKey: bufferedMove.tileKey,
            trigger
        });
        if (!this.tryMove(element, `buffered-${bufferedMove.source}`)) {
            this.clearBufferedMove();
        }
    }

    private tryMove(element: HTMLButtonElement, source: string): boolean {
        if (!this.currentPlayer?.isViewerControlled) {
            logMazeDebug("move ignored because the current view is spectating", {
                source,
                tileKey: element.dataset.tileKey ?? ""
            });
            return false;
        }

        const blocked = element.dataset.blocked === "true";
        const tileKey = element.dataset.tileKey ?? "";

        if (this.optimisticMove && this.queueBufferedMove(element, source)) {
            return true;
        }

        if (blocked || this.isResetAnimating || this.isAdvanceAnimating || this.optimisticMove) {
            logMazeDebug("move blocked", {
                source,
                tileKey,
                blocked,
                isResetAnimating: this.isResetAnimating,
                isAdvanceAnimating: this.isAdvanceAnimating,
                hasOptimisticMove: Boolean(this.optimisticMove)
            });
            return false;
        }

        const x = Number(element.dataset.moveX);
        const y = Number(element.dataset.moveY);
        const target = { x, y };
        const dispatchedAt = Date.now();

        if (tileKey && this.lastDispatchedMoveKey === tileKey && dispatchedAt - this.lastDispatchedMoveAt < DUPLICATE_MOVE_WINDOW_MS) {
            logMazeDebug("duplicate move ignored", {
                source,
                tileKey,
                target
            });
            return false;
        }

        const movePerf = this.createMovePerfSample(source, tileKey, target);
        this.pendingMovePerf = movePerf;
        this.activeMovePerf = movePerf;
        this.measureActiveMovePhase("hidePassageText", () => {
            // Keep the inline passage out of the way as soon as real play starts.
            this.hidePassageTextForMove();
        });
        this.lastDispatchedMoveKey = tileKey;
        this.lastDispatchedMoveAt = dispatchedAt;

        this.measureActiveMovePhase("recordAttempt", () => {
            this.recordAttempt(target);
        });
        this.measureActiveMovePhase("clearPressedTiles", () => {
            this.clearPressedTiles();
        });
        this.measureActiveMovePhase("optimisticMoveSetup", () => {
            this.startOptimisticMove(target);
        });
        logMazeDebug("dispatching move", {
            source,
            tileKey,
            target,
            word: element.querySelector(".tile-word")?.textContent ?? "",
            nextStep: element.dataset.nextStep === "true"
        });
        this.measureActiveMovePhase("sendMoveMessage", () => {
            this.onMove(target);
        });
        const visibleTileCount = this.currentPlayer?.visibleTiles.length ?? 0;
        this.logMovePerf("move-dispatched", movePerf, {
            visibleTiles: visibleTileCount,
            isCoarsePointer: window.matchMedia("(pointer: coarse)").matches
        });
        this.scheduleMoveFramePerf(movePerf, visibleTileCount);
        this.activeMovePerf = null;
        return true;
    }

    private clearSpectatorRotation(resetSelection = true) {
        if (this.spectatorRotationTimer !== null) {
            window.clearTimeout(this.spectatorRotationTimer);
            this.spectatorRotationTimer = null;
        }

        if (resetSelection) {
            this.spectatorPlayerSessionId = null;
            this.spectatorRotateAt = 0;
        }
    }

    private isHostSpectatorRoom(room: RoomView): boolean {
        return (
            room.viewer.isHost &&
            room.phase === "active" &&
            room.spectatorPlayers.length > 0 &&
            room.player !== null &&
            !room.player.isViewerControlled
        );
    }

    private getVisibleParticipants(room: RoomView): RoomParticipantView[] {
        const participants = room.viewer.isHost
            ? room.participants.filter((participant) => participant.sessionId !== room.viewer.sessionId)
            : room.participants;

        return room.phase === "active" && room.mode === RELIC_RUN_MODE
            ? orderRelicRunParticipantsByTime(participants)
            : participants;
    }

    private scheduleSpectatorRotation(candidateCount: number) {
        if (this.spectatorRotationTimer !== null) {
            window.clearTimeout(this.spectatorRotationTimer);
            this.spectatorRotationTimer = null;
        }

        if (candidateCount <= 1 || this.spectatorRotateAt <= 0) {
            return;
        }

        const delay = Math.max(0, this.spectatorRotateAt - Date.now());

        this.spectatorRotationTimer = window.setTimeout(() => {
            if (!this.currentRoom || !this.isHostSpectatorRoom(this.currentRoom)) {
                this.clearSpectatorRotation();
                return;
            }

            const player = this.resolveDisplayedPlayer(this.currentRoom, {
                advance: true
            });
            this.applyRoom(this.currentRoom, player);
        }, delay);
    }

    private resolveDisplayedPlayer(
        room: RoomView,
        options?: {
            advance?: boolean;
        }
    ): PlayerMazeView | null {
        const defaultPlayer = room.player ? clonePlayerView(room.player) : null;

        if (!this.isHostSpectatorRoom(room)) {
            this.clearSpectatorRotation();
            return defaultPlayer;
        }

        const candidates = room.spectatorPlayers.map((player) => clonePlayerView(player));

        if (candidates.length === 0) {
            this.clearSpectatorRotation();
            return defaultPlayer;
        }

        let index = candidates.findIndex((candidate) => candidate.sessionId === this.spectatorPlayerSessionId);

        if (index < 0) {
            index = room.player
                ? candidates.findIndex((candidate) => candidate.sessionId === room.player?.sessionId)
                : -1;
            index = index >= 0 ? index : 0;
            this.spectatorPlayerSessionId = candidates[index]?.sessionId ?? null;
            this.spectatorRotateAt = Date.now() + HOST_SPECTATOR_ROTATE_MS;
        } else if (options?.advance && candidates.length > 1) {
            index = (index + 1) % candidates.length;
            this.spectatorPlayerSessionId = candidates[index]?.sessionId ?? null;
            this.spectatorRotateAt = Date.now() + HOST_SPECTATOR_ROTATE_MS;
        } else if (this.spectatorRotateAt <= 0) {
            this.spectatorRotateAt = Date.now() + HOST_SPECTATOR_ROTATE_MS;
        }

        this.scheduleSpectatorRotation(candidates.length);
        return candidates[index] ?? null;
    }

    private applyRoom(room: RoomView, player: PlayerMazeView | null) {
        const switchedViewedPlayer =
            Boolean(player && this.currentPlayer && this.currentPlayer.sessionId !== player.sessionId);
        const isFreshReset =
            !switchedViewedPlayer &&
            player?.lastEvent?.kind === "reset" &&
            player.lastEvent.id !== this.lastHandledResetId;
        const isFreshAdvance =
            !switchedViewedPlayer &&
            player?.lastEvent?.kind === "advance" &&
            player.lastEvent.id !== this.lastHandledAdvanceId;

        if (this.isResetAnimating || this.isAdvanceAnimating) {
            this.pendingRoom = room;
            return;
        }

        this.syncPassage(room, player);
        this.updateStaticUi(room, player, {
            deferLevelTint: Boolean(isFreshAdvance && this.currentPlayer && !switchedViewedPlayer)
        });

        if (!player) {
            return;
        }

        if (switchedViewedPlayer && this.optimisticMove) {
            this.clearOptimisticMoveTimer();
            this.optimisticMove = null;
            this.pendingMovePerf = null;
            this.activeMovePerf = null;
        }

        logMazeDebug("received room state", {
            phase: room.phase,
            viewedSessionId: player.sessionId,
            progressIndex: player.progressIndex,
            position: player.position,
            visibleTiles: player.visibleTiles.length,
            viewerControlled: player.isViewerControlled,
            lastEvent: player.lastEvent?.kind ?? null
        });

        if (isFreshReset && this.currentPlayer) {
            this.playResetAnimation(player);
            return;
        }

        if (isFreshAdvance && this.currentPlayer) {
            this.playAdvanceAnimation(player);
            return;
        }

        if (this.optimisticMove) {
            if (samePosition(player.position, this.optimisticMove.target)) {
                logMazeDebug("optimistic move confirmed", {
                    from: this.optimisticMove.from,
                    target: this.optimisticMove.target
                });
                this.finishOptimisticMove(player);
                return;
            }

            if (samePosition(player.position, this.optimisticMove.from)) {
                if (this.pendingMovePerf && !this.pendingMovePerf.loggedAwaitingConfirmation) {
                    this.pendingMovePerf.loggedAwaitingConfirmation = true;
                    this.logMovePerf("move-awaiting-confirmation", this.pendingMovePerf, {
                        roomPosition: clonePosition(player.position)
                    });
                }
                this.pendingRoom = room;
                logMazeDebug("optimistic move awaiting confirmation", {
                    from: this.optimisticMove.from,
                    target: this.optimisticMove.target
                });
                return;
            }

            this.optimisticMove = null;
        }

        if (
            !switchedViewedPlayer &&
            this.isEntryAnimating &&
            this.currentPlayer &&
            !player.completed &&
            player.levelNumber === this.currentPlayer.levelNumber &&
            samePosition(player.position, this.currentPlayer.position)
        ) {
            this.refreshPlayerStateDuringEntry(player);
            return;
        }

        this.applyPlayerState(player);
    }

    setRoom(room: RoomView) {
        if (typeof room.serverNow === "number" && Number.isFinite(room.serverNow)) {
            this.serverOffsetMs = room.serverNow - Date.now();
        }

        this.currentRoom = room;
        const player = this.resolveDisplayedPlayer(room);
        this.applyRoom(room, player);
    }

    getCanvasRendererDiagnostics(): MazeCanvasRendererDiagnostics {
        return this.boardRenderer.getDiagnostics();
    }

    destroy() {
        this.cancelPathDrag();
        window.removeEventListener("resize", this.handleWindowResize);
        window.removeEventListener("keydown", this.handleWindowKeyDown);
        this.resizeObserver?.disconnect();
        this.clearOptimisticMoveTimer();
        this.clearRosterLayoutFrame();
        this.clearSpectatorRotation();
        this.clearAnimationTimers();
        this.clearBufferedMove();
        if (this.avatarVisibilityGuardTimer !== null) {
            window.clearTimeout(this.avatarVisibilityGuardTimer);
            this.avatarVisibilityGuardTimer = null;
        }
        if (this.avatarVisibilityGuardFrame !== null) {
            window.cancelAnimationFrame(this.avatarVisibilityGuardFrame);
            this.avatarVisibilityGuardFrame = null;
        }
        if (this.timerRefreshTimer !== null) {
            window.clearTimeout(this.timerRefreshTimer);
            this.timerRefreshTimer = null;
        }
        this.avatarAnimation?.cancel();
        this.avatarAnimation = null;
        this.clearPressedTiles();
        this.resetStageEffects();
        this.boardRenderer.destroy();
        this.root.innerHTML = "";
        this.tiles.clear();
        this.collapsedTileKeys.clear();
        this.visibleTiles = [];
        this.currentPlayer = null;
        this.currentRoom = null;
        this.pendingRoom = null;
        this.visualCenter = null;
        this.avatarPosition = null;
        this.optimisticMove = null;
        this.appliedLevelTintSignature = "";
        this.lastHandledAdvanceId = 0;
        this.isAdvanceAnimating = false;
    }

    private clearRosterLayoutFrame() {
        if (this.rosterLayoutFrameId !== null) {
            window.cancelAnimationFrame(this.rosterLayoutFrameId);
            this.rosterLayoutFrameId = null;
        }
    }

    private scheduleRosterLayoutSync() {
        if (!this.currentRoom?.viewer.isHost || this.rosterSection.hidden) {
            this.clearRosterLayoutFrame();
            return;
        }

        if (this.rosterLayoutFrameId !== null) {
            return;
        }

        const sync = () => {
            this.rosterLayoutFrameId = null;
            syncPlayerRosterLayout({
                hidden: this.rosterSection.hidden,
                rosterBody: this.rosterBody,
                rosterList: this.rosterBody.querySelector<HTMLElement>(".roster-list")
            });
        };

        if (typeof window.requestAnimationFrame === "function") {
            this.rosterLayoutFrameId = window.requestAnimationFrame(sync);
        } else {
            sync();
        }
    }

    private updateTimerChip(player: PlayerMazeView | null) {
        const elapsedText = formatRunTime(getLiveRunElapsedMs(player, this.serverOffsetMs));
        const bestText =
            player?.runPersonalBestMs !== null && player?.runPersonalBestMs !== undefined
                ? formatRunTime(player.runPersonalBestMs)
                : "";
        const nextText = bestText ? `${elapsedText} / ${bestText}` : elapsedText;

        if (this.timerChip.textContent !== nextText) {
            this.timerChip.textContent = nextText;
        }

        this.timerChip.classList.toggle("has-best", Boolean(bestText));
        this.timerChip.classList.toggle("is-paused", !player?.runTimerStartedAt);
        this.timerChip.hidden = !player;
    }

    private scheduleTimerRefresh(player: PlayerMazeView | null) {
        if (this.timerRefreshTimer !== null) {
            window.clearTimeout(this.timerRefreshTimer);
            this.timerRefreshTimer = null;
        }

        if (!player || typeof player.runTimerStartedAt !== "number" || !Number.isFinite(player.runTimerStartedAt)) {
            return;
        }

        this.timerRefreshTimer = window.setTimeout(() => {
            this.timerRefreshTimer = null;
            this.updateTimerChip(this.currentPlayer);
            this.scheduleTimerRefresh(this.currentPlayer);
        }, 96);
    }

    private notifyReadyForTiming(reason: string, player: PlayerMazeView | null = this.currentPlayer) {
        if (!player?.isViewerControlled || player.completed) {
            return;
        }

        if (typeof player.runTimerStartedAt === "number" && Number.isFinite(player.runTimerStartedAt)) {
            return;
        }

        const signature = [
            player.sessionId,
            player.levelNumber,
            player.progressIndex,
            positionKey(player.position),
            player.lastEvent?.id ?? 0
        ].join(":");
        const now = performance.now();

        if (signature === this.lastReadySignature && now - this.lastReadySentAt < 1000) {
            return;
        }

        this.lastReadySignature = signature;
        this.lastReadySentAt = now;
        logMazeDebug("timer ready", { reason, signature });
        this.onReady();
    }

    private updateStaticUi(
        room: RoomView,
        player: PlayerMazeView | null,
        options?: { deferLevelTint?: boolean }
    ) {
        const activePassage = (player?.passageText ?? room.setup.sourceText).trim();
        const progressCount = player ? `${player.completedCount}/${player.totalWords}` : "0/0";
        const progressPercent =
            player && player.totalWords > 0
                ? Math.round((player.completedCount / player.totalWords) * 100)
                : 0;
        const levelNumber = player?.levelNumber ?? 1;
        const visibleParticipants = this.getVisibleParticipants(room);
        const highlightedSessionId =
            room.viewer.isHost && player && !player.isViewerControlled ? player.sessionId : null;
        this.boardInputEnabled = Boolean(player?.isViewerControlled && !player.completed);

        if (!options?.deferLevelTint) {
            this.applyLevelTint(levelNumber);
        }

        const viewerBadgeText =
            player && !player.isViewerControlled
                ? `${t("watchingPlayer", { name: player.playerName })} / ${room.roomCode}`
                : `${room.viewer.name} / ${room.roomCode}`;
        const levelLabelText = player
            ? `Lvl ${player.levelNumber} / ${player.levelCount}`
            : "Lvl 1 / 1";
        const levelTitle = localizeModeDescription(room.mode) ?? RELIC_RUN_DESCRIPTION;
        const showTextAgain = t("showTextAgain");
        const roomRoster = t("roomRoster");
        const progressWidth = `${progressPercent}%`;
        const progressValueText = `${progressCount} ${t("progress")}`;
        const rosterSignature = JSON.stringify([
            room.viewer.isHost,
            highlightedSessionId,
            Boolean(this.onPromptRenameParticipant),
            Boolean(this.onKickParticipant),
            this.openRosterActionsKey,
            visibleParticipants.map((participant) => [
                participant.sessionId,
                participant.name,
                participant.connected,
                participant.completed,
                participant.placement ?? null,
                participant.relicRunBestMs ?? null
            ])
        ]);

        this.layout.classList.toggle("is-host-view", room.viewer.isHost);
        this.layout.classList.toggle("is-host-spectating", Boolean(highlightedSessionId));
        this.stage.classList.toggle("is-viewer-controlled", this.boardInputEnabled);

        if (this.viewerBadge.textContent !== viewerBadgeText) {
            this.viewerBadge.textContent = viewerBadgeText;
        }

        if (this.levelLabel.textContent !== levelLabelText) {
            this.levelLabel.textContent = levelLabelText;
        }

        if (this.levelLabel.getAttribute("title") !== levelTitle) {
            this.levelLabel.setAttribute("title", levelTitle);
        }

        if (this.passageButton.textContent !== showTextAgain) {
            this.passageButton.textContent = showTextAgain;
        }

        this.updatePassageUi(player, activePassage);
        this.updateTimerChip(player);
        this.scheduleTimerRefresh(player);

        if (this.rosterLabel.textContent !== roomRoster) {
            this.rosterLabel.textContent = roomRoster;
        }

        if (this.rosterSection.getAttribute("aria-label") !== roomRoster) {
            this.rosterSection.setAttribute("aria-label", roomRoster);
        }

        const rosterCountText = String(
            visibleParticipants.reduce((count, participant) => count + (participant.connected ? 1 : 0), 0)
        );
        const playerHistoryMarkup = renderPlayerHistoryHeaderMenu(room, rosterCountText, {
            open: this.playerHistoryMenuOpen
        });

        if (this.rosterHistorySlot.innerHTML !== playerHistoryMarkup) {
            this.rosterHistorySlot.innerHTML = playerHistoryMarkup;
        }

        if (this.progressFill.style.width !== progressWidth) {
            this.progressFill.style.width = progressWidth;
        }

        if (this.progressBar.getAttribute("aria-valuenow") !== String(progressPercent)) {
            this.progressBar.setAttribute("aria-valuenow", String(progressPercent));
        }

        if (this.progressBar.getAttribute("aria-valuetext") !== progressValueText) {
            this.progressBar.setAttribute("aria-valuetext", progressValueText);
        }

        this.rosterSection.hidden = !room.viewer.isHost;

        if (this.rosterSignature !== rosterSignature) {
            this.rosterSignature = rosterSignature;
            this.rosterBody.innerHTML = participantMarkup(visibleParticipants, highlightedSessionId, {
                allowHostPromptRename: room.viewer.isHost && Boolean(this.onPromptRenameParticipant),
                allowHostKick: room.viewer.isHost && Boolean(this.onKickParticipant),
                openActionsKey: this.openRosterActionsKey
            });
        }
        this.scheduleRosterLayoutSync();
    }

    private applyLevelTint(levelNumber: number) {
        const tint = levelNumber <= 1 ? "255 255 255" : LEVEL_TINTS[(levelNumber * 5 + 1) % LEVEL_TINTS.length];
        const stageWash = levelNumber <= 1 ? "0" : LEVEL_STAGE_WASH;
        const stageGrid = levelNumber <= 1 ? "0" : LEVEL_STAGE_GRID;
        const tileWash = levelNumber <= 1 ? "0" : LEVEL_TILE_WASH;
        const signature = `${tint}|${stageWash}|${stageGrid}|${tileWash}`;

        if (this.appliedLevelTintSignature === signature) {
            return;
        }

        this.appliedLevelTintSignature = signature;
        this.setRootStyleIfChanged("--maze-level-tint-rgb", tint);
        this.setRootStyleIfChanged("--maze-level-stage-wash", stageWash);
        this.setRootStyleIfChanged("--maze-level-stage-grid", stageGrid);
        this.setRootStyleIfChanged("--maze-level-tile-wash", tileWash);
        this.boardRenderer.setLevelTint(tint);
    }

    private setRootStyleIfChanged(propertyName: string, value: string) {
        if (this.root.style.getPropertyValue(propertyName).trim() === value) {
            return;
        }

        this.root.style.setProperty(propertyName, value);
    }

    private syncPassage(room: RoomView, nextPlayer: PlayerMazeView | null) {
        const nextPassage = (nextPlayer?.passageText ?? room.setup.sourceText).trim();
        const passageChanged = nextPassage !== this.passageText;
        const restartingRun =
            nextPlayer !== null && !nextPlayer.completed && Boolean(this.currentPlayer?.completed);

        if (passageChanged || restartingRun) {
            this.isPassageVisible = Boolean(nextPassage);
        }

        this.passageText = nextPassage;
        this.passagePreviewCache = nextPassage ? createPassagePreviewCache(nextPassage) : null;

        if (!this.passageText) {
            this.isPassageVisible = false;
            return;
        }

        if (!this.currentPlayer && nextPlayer && !nextPlayer.completed) {
            this.isPassageVisible = true;
        }
    }

    private updatePassageUi(player: PlayerMazeView | null, activePassage: string = this.passageText) {
        const shouldShowInline = Boolean(activePassage) && this.isPassageVisible && !player?.completed;
        const buttonDisabled =
            !activePassage ||
            Boolean(player?.completed) ||
            this.isPassageVisible ||
            this.isEntryAnimating ||
            this.isResetAnimating ||
            this.isAdvanceAnimating ||
            Boolean(this.optimisticMove);

        if (this.passageButton.disabled !== buttonDisabled) {
            this.passageButton.disabled = buttonDisabled;
        }

        if (!shouldShowInline) {
            this.passageInline.hidden = true;
            this.passageInline.classList.remove("is-truncated");
            this.passageInline.dataset.truncated = "false";
            this.passageInline.textContent = "";
            return;
        }

        const preview = this.getPassagePreview(activePassage, Math.max(0, player?.progressIndex ?? 0));

        if (this.passageInline.textContent !== preview.text) {
            this.passageInline.textContent = preview.text;
        }

        this.passageInline.classList.toggle("is-truncated", preview.truncated);
        this.passageInline.dataset.truncated = preview.truncated ? "true" : "false";
        this.passageInline.hidden = false;
    }

    private applyPlayerState(player: PlayerMazeView) {
        const previousPosition = this.currentPlayer?.position ?? player.position;
        const moved = !samePosition(previousPosition, player.position);
        const levelChanged = this.currentPlayer?.levelNumber !== player.levelNumber;
        const switchedViewedPlayer =
            Boolean(this.currentPlayer) && this.currentPlayer?.sessionId !== player.sessionId;
        this.clearAnimationTimers();
        this.clearOptimisticMoveTimer();
        this.resetStageEffects();
        this.clearBufferedMove();
        this.resetReplayState(player);
        this.applyAvatarAppearance(player);

        if (levelChanged || switchedViewedPlayer || (this.currentPlayer?.completed && !player.completed)) {
            this.collapsedTileKeys.clear();
            this.visualCenter = null;
            this.avatarPosition = null;
            this.cancelPathDrag();
        }

        this.syncTiles(player.visibleTiles, player.position);
        this.setCamera(player.position, moved && !switchedViewedPlayer);

        if (switchedViewedPlayer) {
            this.resetAvatarTransform();
        } else if (!this.currentPlayer && !player.completed) {
            this.playEntryAnimation(player);
        } else if (moved) {
            this.animateHop(previousPosition, player.position);
        } else {
            this.resetAvatarTransform();
        }

        this.updateOverlay(player);
        this.currentPlayer = player;
        this.updatePassageUi(player);
        this.reportVisibleTileAnomaly(player);
        this.optimisticMove = null;
        this.lastAttemptedMove = null;
        this.lastDispatchedMoveKey = "";
        this.lastDispatchedMoveAt = 0;
        this.scheduleAvatarVisibilityGuard(
            "apply-player-state",
            moved && !switchedViewedPlayer ? HOP_DURATION_MS + 48 : 48
        );

        if (!this.isEntryAnimating && !this.isAdvanceAnimating && !this.isResetAnimating && !this.optimisticMove) {
            this.notifyReadyForTiming("apply-player-state", player);
        }
    }

    private finishOptimisticMove(player: PlayerMazeView) {
        const movePerf = this.pendingMovePerf;

        if (this.currentPlayer?.levelNumber !== player.levelNumber) {
            this.collapsedTileKeys.clear();
        }

        this.clearOptimisticMoveTimer();
        this.clearAnimationTimers();
        this.resetStageEffects();
        this.resetReplayState(player);
        this.applyAvatarAppearance(player);
        this.activeMovePerf = movePerf;
        this.measureActiveMovePhase("syncAuthoritativeTiles", () => {
            this.syncTiles(player.visibleTiles, player.position);
        });
        this.measureActiveMovePhase("setAuthoritativeCamera", () => {
            // An acknowledgement must not truncate a hop or restart an edge pan.
            if (!this.avatarPosition || !samePosition(this.avatarPosition, player.position)) {
                this.setCamera(player.position, false);
            }
        });
        this.measureActiveMovePhase("updateOverlay", () => {
            this.updateOverlay(player);
        });
        this.currentPlayer = player;
        this.updatePassageUi(player);
        this.reportVisibleTileAnomaly(player);
        this.optimisticMove = null;
        this.lastAttemptedMove = null;
        this.lastDispatchedMoveKey = "";
        this.lastDispatchedMoveAt = 0;
        this.activeMovePerf = null;
        this.updatePassageUi(player);
        this.finalizePendingMovePerf("move-confirmed", {
            progressIndex: player.progressIndex,
            levelNumber: player.levelNumber,
            event: player.lastEvent?.kind ?? null
        });
        this.scheduleAvatarVisibilityGuard("finish-optimistic-move", 48);
        this.flushBufferedMoveIfPossible("move-confirmed");
    }

    private resetReplayState(player: PlayerMazeView) {
        if (this.currentPlayer?.completed && !player.completed) {
            this.collapsedTileKeys.clear();
            this.isReplayPending = false;
            return;
        }

        if (!player.completed) {
            this.isReplayPending = false;
        }
    }

    private applyAvatarAppearance(player: PlayerMazeView) {
        this.applyEntryCorner(player);
        const isPaperWhite = player.avatarHue === PAPER_WHITE_AVATAR_HUE;
        this.avatar.classList.toggle("is-paper-white", isPaperWhite);
        if (!isPaperWhite) {
            this.avatar.style.setProperty("--avatar-hue", String(player.avatarHue));
        }
        this.avatar.style.opacity = "1";
        setCapyShieldHue(this.avatar.querySelector<HTMLElement>(".maze-capy")!, player.avatarHue);
        this.avatarLayer.classList.remove("is-submerged");
    }

    private applyEntryCorner(player: PlayerMazeView) {
        this.entryCorner = player.entryCorner ?? "top-left";
        this.courseBounds = player.courseBounds;
        this.coursePlacement = player.coursePlacement;
        this.stage.classList.toggle("is-top-entry", this.entryCorner === "top-left");
        this.stage.classList.toggle("is-bottom-entry", this.entryCorner === "bottom-left");
    }

    private refreshPlayerStateDuringEntry(player: PlayerMazeView) {
        this.clearBufferedMove();
        this.resetReplayState(player);
        this.applyAvatarAppearance(player);
        this.syncTiles(player.visibleTiles, player.position);
        this.setCamera(player.position, false);
        this.updateOverlay(player);
        this.currentPlayer = player;
        this.updatePassageUi(player);
        this.lastAttemptedMove = null;
        this.lastDispatchedMoveKey = "";
        this.lastDispatchedMoveAt = 0;
    }

    private buildTileClassName(tile: MazeTileView): string {
        return [
            "maze-tile",
            `maze-tile-${tile.kind}`,
            tile.isCurrent ? "is-current" : "",
            tile.isReachable ? "is-reachable" : "",
            tile.isNextStep ? "is-next-step" : ""
        ]
            .filter(Boolean)
            .join(" ");
    }

    private buildTileMark(tile: MazeTileView): string {
        if (tile.isCurrent || tile.kind === "start" || tile.kind === "portal" || tile.kind === "trail") {
            return "";
        }

        if (tile.kind === "end") {
            return t("tileEnd");
        }

        return "";
    }

    private buildRenderTile(tile: MazeTileView): MazeBoardRenderTile {
        const runner = this.avatarPosition ?? this.currentPlayer?.position ?? tile;
        const revealed = isMazePositionRevealed(tile, runner) &&
            (Boolean(tile.word) || tile.kind === "start" || tile.isCurrent);
        // Keep the full cell reach, but soften its square corners into the darkness.
        const outerDistance = Math.max(0, Math.min(1,
            (Math.hypot(tile.x - runner.x, tile.y - runner.y) - MAZE_REVEAL_RADIUS) /
            (MAZE_REVEAL_RADIUS * (Math.SQRT2 - 1))));
        const revealOpacity = revealed
            ? 1 - outerDistance * (1 - SITE_TUNING.relicRun.reveal.outerDiagonalOpacity)
            : SITE_TUNING.relicRun.reveal.unrevealedTileOpacity;
        return {
            ...tile,
            kind: revealed ? tile.kind : "decoy",
            isNextStep: revealed && tile.isNextStep,
            revealed,
            revealOpacity,
            blocked:
                !this.boardInputEnabled ||
                !tile.isReachable ||
                this.isResetAnimating ||
                this.isAdvanceAnimating,
            displayMark: revealed ? this.buildTileMark(tile) : "",
            displayWord: !revealed || tile.kind === "start" || tile.isCurrent ? "" : tile.word,
            pressed: false
        };
    }

    private syncCanvasBoard() {
        this.boardRenderer.setTiles(this.visibleTiles.map((tile) => this.buildRenderTile(tile)));
    }

    private updateTileElement(
        element: HTMLButtonElement,
        tile: MazeTileView
    ) {
        const revealed = this.buildRenderTile(tile).revealed;
        const nextClassName = this.buildTileClassName(tile) + (revealed ? "" : " is-fogged");

        if (element.className !== nextClassName) {
            element.className = nextClassName;
        }

        const blocked =
            !revealed ||
            !this.boardInputEnabled ||
            !tile.isReachable ||
            this.isResetAnimating ||
            this.isAdvanceAnimating;
        const nextBlocked = String(blocked);
        const nextStep = String(tile.isNextStep);
        const ariaDisabled = blocked ? "true" : "false";
        const tileWordElement = element.firstElementChild as HTMLElement | null;
        const tileMarkElement = element.lastElementChild as HTMLElement | null;
        const tileWord = !revealed || tile.kind === "start" || tile.isCurrent ? "" : tile.word;
        const tileMark = revealed ? this.buildTileMark(tile) : "";
        const ariaHidden = revealed ? "false" : "true";
        if (element.getAttribute("aria-hidden") !== ariaHidden) element.setAttribute("aria-hidden", ariaHidden);

        if (element.dataset.blocked !== nextBlocked) {
            element.dataset.blocked = nextBlocked;
        }

        if (element.dataset.nextStep !== nextStep) {
            element.dataset.nextStep = nextStep;
        }

        if (element.getAttribute("aria-disabled") !== ariaDisabled) {
            element.setAttribute("aria-disabled", ariaDisabled);
        }

        const tabIndex = blocked ? -1 : 0;

        if (element.tabIndex !== tabIndex) {
            element.tabIndex = tabIndex;
        }

        if (tileWordElement && tileWordElement.textContent !== tileWord) {
            tileWordElement.textContent = tileWord;
        }

        if (tileMarkElement && tileMarkElement.textContent !== tileMark) {
            tileMarkElement.textContent = tileMark;
        }
    }

    private syncTiles(tiles: MazeTileView[], center: GridPosition) {
        this.avatarPosition = clonePosition(center);
        const nextKeys = new Set<string>();
        const fragment = document.createDocumentFragment();
        const nextVisibleTiles: MazeTileView[] = [];

        for (const tile of tiles) {
            if (this.collapsedTileKeys.has(tile.key) && !tile.isCurrent) {
                continue;
            }

            nextKeys.add(tile.key);
            nextVisibleTiles.push(tile);
            let element = this.tiles.get(tile.key);

            if (!element) {
                element = document.createElement("button");
                const tileButton = element;
                element.type = "button";
                element.className = "maze-tile";
                element.innerHTML = `
                    <span class="tile-word"></span>
                    <span class="tile-mark"></span>
                `;
                element.addEventListener("keydown", (event) => {
                    if (event.key !== "Enter" && event.key !== " ") {
                        return;
                    }

                    event.preventDefault();
                    this.logTileEvent("keydown", tileButton);
                    this.tryMove(tileButton, "keydown");
                });
                element.dataset.moveX = String(tile.x);
                element.dataset.moveY = String(tile.y);
                element.dataset.tileKey = tile.key;
                element.style.setProperty("--tile-x", `${tile.x * CELL_SIZE}px`);
                element.style.setProperty("--tile-y", `${tile.y * CELL_SIZE}px`);
                this.tiles.set(tile.key, element);
                fragment.appendChild(element);
            }

            this.updateTileElement(element, tile);
        }

        this.board.appendChild(fragment);

        for (const [key, element] of this.tiles.entries()) {
            if (nextKeys.has(key)) {
                continue;
            }

            if (element === this.pressedTile) {
                this.pressedTile = null;
            }
            element.remove();
            this.tiles.delete(key);
        }

        this.visibleTiles = nextVisibleTiles;
        this.syncCanvasBoard();
    }

    private setBoardView(
        position: GridPosition,
        options?: {
            animate?: boolean;
            shiftX?: number;
            shiftY?: number;
            tiltDeg?: number;
            opacity?: number;
            durationMs?: number;
            easing?: string;
        }
    ) {
        const animate = (options?.animate ?? false) && !window.matchMedia("(prefers-reduced-motion: reduce)").matches;
        const durationMs = options?.durationMs ?? CAMERA_MOVE_MS;
        const easing = options?.easing ?? CAMERA_EASING;
        const boardScale = this.getBoardScale();
        const stageWidth = this.stageWidth || this.stage.clientWidth;
        const stageHeight = this.stageHeight || this.stage.clientHeight;
        const boardTranslateX = (stageWidth / 2 - position.x * CELL_SIZE) * boardScale;
        const boardTranslateY = (stageHeight / 2 - position.y * CELL_SIZE) * boardScale;

        this.boardShell.style.transition = animate
            ? `transform ${durationMs}ms ${easing}, opacity ${durationMs}ms ${easing}`
            : "none";
        this.boardShell.style.setProperty("--board-translate-x", `${boardTranslateX}px`);
        this.boardShell.style.setProperty("--board-translate-y", `${boardTranslateY}px`);
        this.boardShell.style.setProperty("--board-shift-x", `${options?.shiftX ?? 0}px`);
        this.boardShell.style.setProperty("--board-shift-y", `${options?.shiftY ?? 0}px`);
        this.boardShell.style.setProperty("--board-tilt", `${options?.tiltDeg ?? 0}deg`);
        this.boardShell.style.opacity = String(options?.opacity ?? 1);
        this.visualCenter = clonePosition(position);
    }

    private scheduleAnimationStep(callback: () => void, delayMs: number): number {
        const timer = window.setTimeout(() => {
            this.animationTimers.delete(timer);
            callback();
        }, Math.max(0, delayMs));
        this.animationTimers.add(timer);
        return timer;
    }

    private clearAnimationTimers() {
        for (const timer of this.animationTimers) {
            window.clearTimeout(timer);
        }

        this.animationTimers.clear();
    }

    private setCamera(position: GridPosition, animate: boolean) {
        const cellSize = this.getVisualCellSize();
        const camera = this.visualCenter ? followRelicRunner(this.visualCenter, position, {
            width: this.stageWidth, height: this.stageHeight, cellSize, corner: this.entryCorner, bounds: this.courseBounds
        }) : startRelicCamera(position, { width: this.stageWidth, height: this.stageHeight, cellSize, corner: this.entryCorner, bounds: this.courseBounds, placement: this.coursePlacement });
        if (!this.visualCenter || !samePosition(camera, this.visualCenter)) {
            if (this.visualCenter && this.touchSwipe) this.touchSwipe.cameraMoved = true;
            this.setBoardView(camera, { animate });
        }
        this.avatarPosition = clonePosition(position);
        this.positionAvatar(position, camera, animate);
    }

    private positionAvatar(position: GridPosition, camera: GridPosition, animate: boolean) {
        const x = (position.x - camera.x) * this.getVisualCellSize();
        const y = (position.y - camera.y) * this.getVisualCellSize();
        const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
        this.avatarLayer.style.transition = animate && !reducedMotion ? `transform ${HOP_DURATION_MS}ms linear` : "none";
        this.avatarLayer.style.transform = `translate(${x}px, ${y}px)`;
        this.stage.style.setProperty("--maze-player-x", `${x}px`);
        this.stage.style.setProperty("--maze-player-y", `${y}px`);
        this.stage.style.setProperty("--maze-light-x", `${this.stageWidth / 2 + x}px`);
        this.stage.style.setProperty("--maze-light-y", `${this.stageHeight / 2 + y}px`);
        this.stage.style.setProperty("--maze-floor-x", `${this.stageWidth / 2 - (camera.x + 0.5) * this.getVisualCellSize()}px`);
        this.stage.style.setProperty("--maze-floor-y", `${this.stageHeight / 2 - (camera.y + 0.5) * this.getVisualCellSize()}px`);
    }

    private refreshStageMetrics() {
        this.stageWidth = this.stage.clientWidth;
        this.stageHeight = this.stage.clientHeight;
        const tuning = SITE_TUNING.relicRun;
        const preferredScale = window.matchMedia("(pointer: coarse)").matches ? tuning.board.mobileBoardScale : tuning.board.desktopBoardScale;
        this.boardScale = Math.max(tuning.camera.minBoardScale, Math.min(preferredScale,
            this.stageWidth / (CELL_SIZE * tuning.camera.viewportCells),
            this.stageHeight / (CELL_SIZE * tuning.camera.viewportCells)));
        this.stage.style.setProperty("--maze-board-scale", String(this.boardScale));
        this.boardRenderer.refresh();
        this.scheduleAvatarVisibilityGuard("resize");
    }

    private getBoardScale(): number {
        return this.boardScale > 0 ? this.boardScale : 1;
    }

    private getVisualCellSize(): number {
        return CELL_SIZE * this.getBoardScale();
    }

    private getAvatarPixelSize(): number {
        const size = Number.parseFloat(window.getComputedStyle(this.avatar).width);

        return Number.isFinite(size) && size > 0 ? size : 42 * this.getBoardScale();
    }

    private setFallCurtainOpacity(opacity: number) {
        const clampedOpacity = Math.max(0, Math.min(1, opacity));
        this.fallCurtain.getAnimations().forEach((animation) => animation.cancel());
        this.fallCurtain.style.opacity = String(clampedOpacity);
        this.fallCurtain.hidden = clampedOpacity <= 0.001;
    }

    private getFallCurtainOpacity(): number {
        const inlineOpacity = Number.parseFloat(this.fallCurtain.style.opacity);

        if (Number.isFinite(inlineOpacity)) {
            return inlineOpacity;
        }

        const computedOpacity = Number.parseFloat(window.getComputedStyle(this.fallCurtain).opacity);
        return Number.isFinite(computedOpacity) ? computedOpacity : 0;
    }

    private animateFallCurtain(targetOpacity: number, durationMs: number, easing = CAMERA_EASING) {
        const clampedOpacity = Math.max(0, Math.min(1, targetOpacity));
        const computedOpacity = Number.parseFloat(window.getComputedStyle(this.fallCurtain).opacity);
        const startOpacity =
            this.fallCurtain.hidden || !Number.isFinite(computedOpacity) ? 0 : computedOpacity;

        if (durationMs <= 0) {
            this.setFallCurtainOpacity(clampedOpacity);
            return;
        }

        this.fallCurtain.hidden = false;
        this.fallCurtain.getAnimations().forEach((animation) => animation.cancel());
        const animation = this.fallCurtain.animate(
            [{ opacity: startOpacity }, { opacity: clampedOpacity }],
            {
                duration: durationMs,
                easing,
                fill: "forwards"
            }
        );

        animation.finished
            .then(() => {
                this.fallCurtain.style.opacity = String(clampedOpacity);
                this.fallCurtain.hidden = clampedOpacity <= 0.001;
            })
            .catch(() => {
                // Ignore canceled blackout transitions; a newer transition already superseded them.
            });
    }

    private resetStageEffects() {
        if (this.jumpTrailStartTimer !== null) {
            window.clearTimeout(this.jumpTrailStartTimer);
            this.jumpTrailStartTimer = null;
        }
        this.boardRenderer.clearTransientState();
        this.jumpTrailAnimation?.cancel();
        this.jumpTrailAnimation = null;
        this.flare.getAnimations().forEach((animation) => animation.cancel());
        this.fallCurtain.getAnimations().forEach((animation) => animation.cancel());
        this.flare.classList.remove("is-active");
        this.pit.classList.remove("is-active");
        this.avatarLayer.classList.remove("is-submerged");
        this.hideLevelBestCallout();
        this.fallCurtain.style.opacity = "0";
        this.fallCurtain.hidden = true;
    }

    private hideLevelBestCallout() {
        this.levelBestCallout.hidden = true;
        this.levelBestCallout.textContent = "";
        this.levelBestCallout.classList.remove("is-active");
    }

    private showLevelBestCallout(player: PlayerMazeView) {
        const message = buildLevelBestMessage(player.levelBestImprovementMs);

        if (!message) {
            this.hideLevelBestCallout();
            return;
        }

        this.levelBestCallout.textContent = message;
        this.levelBestCallout.hidden = false;
        this.levelBestCallout.classList.remove("is-active");
        void this.levelBestCallout.offsetWidth;
        this.levelBestCallout.classList.add("is-active");
    }

    private scheduleAvatarVisibilityGuard(reason: string, delayMs = 0) {
        if (this.avatarVisibilityGuardTimer !== null) {
            window.clearTimeout(this.avatarVisibilityGuardTimer);
            this.avatarVisibilityGuardTimer = null;
        }

        if (this.avatarVisibilityGuardFrame !== null) {
            window.cancelAnimationFrame(this.avatarVisibilityGuardFrame);
            this.avatarVisibilityGuardFrame = null;
        }

        this.avatarVisibilityGuardTimer = window.setTimeout(() => {
            this.avatarVisibilityGuardTimer = null;
            this.avatarVisibilityGuardFrame = window.requestAnimationFrame(() => {
                this.avatarVisibilityGuardFrame = null;
                this.repairAvatarVisibilityIfNeeded(reason);
            });
        }, Math.max(0, delayMs));
    }

    private repairAvatarVisibilityIfNeeded(reason: string) {
        if (
            !this.root.isConnected ||
            !this.currentPlayer ||
            this.currentPlayer.completed ||
            this.isResetAnimating ||
            this.isAdvanceAnimating ||
            this.isEntryAnimating ||
            this.optimisticMove
        ) {
            return;
        }

        const avatarRect = this.avatar.getBoundingClientRect();
        const stageRect = this.stage.getBoundingClientRect();
        const computedOpacity = Number.parseFloat(window.getComputedStyle(this.avatar).opacity);
        const faded = Number.isFinite(computedOpacity) && computedOpacity < 0.2;
        const submerged = this.avatarLayer.classList.contains("is-submerged");
        const missingBounds = avatarRect.width < 4 || avatarRect.height < 4;
        const offStage =
            avatarRect.right < stageRect.left ||
            avatarRect.left > stageRect.right ||
            avatarRect.bottom < stageRect.top ||
            avatarRect.top > stageRect.bottom;

        if (!submerged && !faded && !missingBounds && !offStage) {
            return;
        }

        this.applyAvatarAppearance(this.currentPlayer);
        this.resetAvatarTransform();
        this.setCamera(this.currentPlayer.position, false);
        logMazePerfDiagnostic(`avatar-visibility-resync reason=${reason}`, {
            avatarOpacity: Number.isFinite(computedOpacity) ? roundPerfMs(computedOpacity) : null,
            avatarRect: {
                height: roundPerfMs(avatarRect.height),
                left: roundPerfMs(avatarRect.left),
                top: roundPerfMs(avatarRect.top),
                width: roundPerfMs(avatarRect.width)
            },
            currentPosition: this.currentPlayer.position,
            offStage,
            reason,
            stageRect: {
                height: roundPerfMs(stageRect.height),
                left: roundPerfMs(stageRect.left),
                top: roundPerfMs(stageRect.top),
                width: roundPerfMs(stageRect.width)
            },
            submerged
        });
    }

    private playAvatarDrop(durationMs: number) {
        const entryDistance = Math.max(this.stage.clientHeight / 2 + this.getAvatarPixelSize() * 1.7, 320);
        const entryStartY = -Math.round(entryDistance);
        const entryMidY = Math.round(entryStartY * 0.36);
        this.avatar.style.transform = `translate(0px, ${entryStartY}px) scale(0.72)`;
        this.avatar.style.opacity = "1";

        this.playAvatarAnimation(
            [
                { transform: `translate(0px, ${entryStartY}px) scale(0.72)`, opacity: 1 },
                {
                    transform: `translate(0px, ${entryMidY}px) scale(0.96)`,
                    opacity: 1,
                    offset: 0.66
                },
                {
                    transform: "translate(0px, 8px) scale(1.08, 0.86)",
                    opacity: 1,
                    offset: 0.88
                },
                { transform: "translate(0px, 0px) scale(1)", opacity: 1 }
            ],
            {
                duration: durationMs,
                easing: "cubic-bezier(0.14, 0.78, 0.18, 1.08)",
                fill: "forwards"
            },
            true
        );
    }

    private recenterBoardAfterResize() {
        if (!this.visualCenter || this.isResetAnimating || this.isAdvanceAnimating) {
            return;
        }

        this.setBoardView(this.visualCenter, { animate: false });
        if (this.avatarPosition) {
            if (this.currentPlayer?.completedCount === 0 && !this.optimisticMove) {
                this.visualCenter = null;
            }
            this.setCamera(this.avatarPosition, false);
        }
    }

    private animateHop(from: GridPosition, to: GridPosition) {
        const dx = to.x - from.x;
        const dy = to.y - from.y;
        const usesCoarsePointer = window.matchMedia("(pointer: coarse)").matches;
        this.avatarLayer.classList.remove("is-submerged");
        this.measureActiveMovePhase("jumpTrailSchedule", () => {
            this.queueJumpTrail(dx, dy, usesCoarsePointer);
        });

        this.measureActiveMovePhase("avatarAnimationSetup", () => {
            const lateralDistance = 0;
            const liftHeight = window.matchMedia("(prefers-reduced-motion: reduce)").matches ? 0 : 5;
            const hopDuration = HOP_DURATION_MS;

            // The avatar layer supplies travel; keep the capy's hop light and quick.
            this.playAvatarAnimation(
                [
                    { transform: "translate(0px, 0px) scale(1)" },
                    {
                        transform: `translate(${dx * lateralDistance}px, ${dy * lateralDistance - liftHeight}px) scale(1.09, 0.92)`,
                        offset: 0.44
                    },
                    { transform: "translate(0px, 0px) scale(1)" }
                ],
                {
                    duration: hopDuration,
                    easing: HOP_EASING
                },
                true
            );
        });
    }

    private queueJumpTrail(dx: number, dy: number, usesCoarsePointer: boolean) {
        if (dx === 0 && dy === 0) {
            return;
        }

        if (this.jumpTrailStartTimer !== null) {
            window.clearTimeout(this.jumpTrailStartTimer);
            this.jumpTrailStartTimer = null;
        }

        const revealDelayMs = SITE_TUNING.relicRun.moveTrail.revealDelayMs;

        if (revealDelayMs <= 0) {
            this.playJumpTrail(dx, dy, usesCoarsePointer);
            return;
        }

        this.jumpTrailStartTimer = window.setTimeout(() => {
            this.jumpTrailStartTimer = null;
            this.playJumpTrail(dx, dy, usesCoarsePointer);
        }, revealDelayMs);
    }

    private playJumpTrail(dx: number, dy: number, usesCoarsePointer: boolean) {
        if (dx === 0 && dy === 0) {
            return;
        }

        const angleDeg = (Math.atan2(dy, dx) * 180) / Math.PI;
        const trailScale = usesCoarsePointer
            ? SITE_TUNING.relicRun.moveTrail.mobileLengthScale
            : SITE_TUNING.relicRun.moveTrail.desktopLengthScale;
        const trailSpan = Math.max(this.getVisualCellSize() * trailScale, 82);
        const trailHeight = usesCoarsePointer
            ? SITE_TUNING.relicRun.moveTrail.mobileThicknessPx
            : SITE_TUNING.relicRun.moveTrail.desktopThicknessPx;
        const trailDurationMs = usesCoarsePointer
            ? SITE_TUNING.relicRun.moveTrail.mobileRevealDurationMs
            : SITE_TUNING.relicRun.moveTrail.desktopRevealDurationMs;

        this.jumpTrail.style.setProperty("--jump-trail-angle", `${angleDeg}deg`);
        this.jumpTrail.style.setProperty("--jump-trail-span", `${trailSpan}px`);
        this.jumpTrail.style.setProperty("--jump-trail-start-x", "0px");
        this.jumpTrail.style.setProperty("--jump-trail-start-y", "0px");
        this.jumpTrail.style.height = `${trailHeight}px`;
        this.jumpTrailAnimation?.cancel();
        this.jumpTrailAnimation = this.jumpTrail.animate(
            [
                {
                    transform: `translateY(-50%) rotate(${angleDeg}deg) scaleX(0.14) scaleY(0.82)`,
                    opacity: 0
                },
                {
                    transform: `translateY(-50%) rotate(${angleDeg}deg) scaleX(0.46) scaleY(0.94)`,
                    opacity: 0.9,
                    offset: 0.2
                },
                {
                    transform: `translateY(-50%) rotate(${angleDeg}deg) scaleX(1) scaleY(1)`,
                    opacity: 0.72,
                    offset: 0.68
                },
                {
                    transform: `translateY(-50%) rotate(${angleDeg}deg) scaleX(1.06) scaleY(0.92)`,
                    opacity: 0
                }
            ],
            {
                duration: trailDurationMs,
                easing: "cubic-bezier(0.2, 0.78, 0.18, 1)",
                fill: "none"
            }
        );
        this.jumpTrailAnimation.finished
            .catch(() => {
                // Ignore canceled jump trail animations when a newer move supersedes them.
            })
            .finally(() => {
                if (this.jumpTrailAnimation?.playState === "finished") {
                    this.jumpTrailAnimation = null;
                }
            });
    }

    private playAvatarAnimation(
        keyframes: Keyframe[],
        options: KeyframeAnimationOptions,
        resetWhenFinished = false
    ): Animation {
        this.avatarAnimationNonce += 1;
        const nonce = this.avatarAnimationNonce;
        // Finished fill-forwards effects still paint even after their handle
        // was released. Retire every previous effect before the next state.
        this.avatar.getAnimations().forEach(animation => animation.cancel());
        this.avatarAnimation = null;
        const animation = this.avatar.animate(keyframes, options);
        this.avatarAnimation = animation;

        if (resetWhenFinished) {
            animation.finished
                .then(() => {
                    if (this.avatarAnimationNonce !== nonce || this.isResetAnimating) {
                        return;
                    }

                    this.resetAvatarTransform();
                })
                .catch(() => {
                    // Ignore canceled animations; a newer avatar animation already took over.
                })
                .finally(() => {
                    if (this.avatarAnimation === animation) {
                        this.avatarAnimation = null;
                    }
                });
        } else {
            animation.finished
                .catch(() => {
                    // Ignore canceled animations; a newer avatar animation already took over.
                })
                .finally(() => {
                    const fill = animation.effect?.getTiming().fill;
                    if (this.avatarAnimation === animation && fill !== "forwards" && fill !== "both") {
                        this.avatarAnimation = null;
                    }
                });
        }

        return animation;
    }

    private captureAvatarFrame(): Keyframe {
        const computedStyle = window.getComputedStyle(this.avatar);
        const opacity = Number.parseFloat(computedStyle.opacity);

        return {
            transform:
                computedStyle.transform && computedStyle.transform !== "none"
                    ? computedStyle.transform
                    : "translate(0px, 0px) scale(1)",
            opacity: Number.isFinite(opacity) ? opacity : 1
        };
    }

    private getOptimisticNextPathTileKey(target: GridPosition): string {
        if (!this.currentPlayer) {
            return "";
        }

        const previousKey = positionKey(this.currentPlayer.position);
        return (
            this.currentPlayer.visibleTiles.find((tile) => {
                if (tile.key === previousKey || manhattanDistance(tile, target) !== 1) {
                    return false;
                }

                return tile.kind === "path" || tile.kind === "end" || tile.kind === "portal";
            })?.key ?? ""
        );
    }

    private buildOptimisticVisibleTile(
        tile: MazeTileView,
        target: GridPosition,
        nextPathTileKey: string
    ): MazeTileView | null {
        if (!this.currentPlayer) {
            return null;
        }

        const previousKey = positionKey(this.currentPlayer.position);
        const targetKey = positionKey(target);
        let kind = tile.kind;

        if (tile.key === previousKey && tile.kind === "path") {
            kind = "trail";
        }

        const isCurrent = tile.key === targetKey;

        return {
            ...tile,
            kind,
            isCurrent,
            isReachable: manhattanDistance(tile, target) === 1 && tile.key !== previousKey,
            isNextStep: !isCurrent && tile.key === nextPathTileKey
        };
    }

    private applyOptimisticMovePreview(target: GridPosition) {
        if (!this.currentPlayer) {
            return;
        }

        this.avatarPosition = clonePosition(target);
        const nextPathTileKey = this.getOptimisticNextPathTileKey(target);
        const optimisticTiles = this.currentPlayer.visibleTiles
            .map((tile) => this.buildOptimisticVisibleTile(tile, target, nextPathTileKey))
            .filter((tile): tile is MazeTileView => Boolean(tile));
        for (const tile of optimisticTiles) {
            const element = this.tiles.get(tile.key);
            if (!element) {
                continue;
            }
            this.updateTileElement(element, tile);
        }

        this.visibleTiles = optimisticTiles;
        this.syncCanvasBoard();
    }

    private startOptimisticMove(target: GridPosition) {
        if (!this.currentPlayer || samePosition(this.currentPlayer.position, target)) {
            return;
        }

        this.clearOptimisticMoveTimer();
        const from = clonePosition(this.currentPlayer.position);
        this.optimisticMove = {
            from,
            target: clonePosition(target),
            startedAt: performance.now()
        };
        this.updatePassageUi(this.currentPlayer);

        this.measureActiveMovePhase("optimisticPreview", () => {
            this.applyOptimisticMovePreview(target);
        });

        this.measureActiveMovePhase("cameraMove", () => {
            this.setCamera(target, true);
        });
        this.measureActiveMovePhase("hopStart", () => {
            this.animateHop(from, target);
        });
        this.optimisticMoveTimer = window.setTimeout(() => {
            this.rollbackOptimisticMove();
        }, OPTIMISTIC_MOVE_TIMEOUT_MS);
    }

    private clearOptimisticMoveTimer() {
        if (this.optimisticMoveTimer !== null) {
            window.clearTimeout(this.optimisticMoveTimer);
            this.optimisticMoveTimer = null;
        }
    }

    private rollbackOptimisticMove() {
        if (!this.optimisticMove) {
            return;
        }
        this.cancelPathDrag();

        logMazeDebug("optimistic move timed out; rolling back to authoritative room state", {
            from: this.optimisticMove.from,
            target: this.optimisticMove.target,
            currentRoomPosition: this.currentRoom?.player?.position ?? null
        });

        this.clearOptimisticMoveTimer();
        this.optimisticMove = null;
        this.lastAttemptedMove = null;
        this.lastDispatchedMoveKey = "";
        this.lastDispatchedMoveAt = 0;
        this.clearBufferedMove();
        this.clearPressedTiles();
        this.finalizePendingMovePerf("move-rollback", {
            currentRoomPosition: this.currentRoom?.player?.position ?? null
        });

        if (this.currentRoom?.player) {
            this.applyPlayerState(clonePlayerView(this.currentRoom.player));
            return;
        }

        this.resetAvatarTransform();
        if (this.visualCenter) {
            this.setCamera(this.visualCenter, false);
        }
        this.disableTiles(false);
        this.updatePassageUi(this.currentPlayer);
    }

    private playResetAnimation(player: PlayerMazeView) {
        this.cancelPathDrag();
        if (!this.currentPlayer) {
            this.lastHandledResetId = player.lastEvent?.id ?? this.lastHandledResetId;
            this.applyPlayerState(player);
            return;
        }

        this.isResetAnimating = true;
        this.clearOptimisticMoveTimer();
        this.clearAnimationTimers();
        this.resetStageEffects();
        this.clearBufferedMove();
        this.lastHandledResetId = player.lastEvent?.id ?? this.lastHandledResetId;
        this.lastDispatchedMoveKey = "";
        this.lastDispatchedMoveAt = 0;
        this.finalizePendingMovePerf("move-reset", {
            progressIndex: player.progressIndex,
            levelNumber: player.levelNumber
        });

        const previousPosition = this.currentPlayer.position;
        const optimisticMove = this.optimisticMove;
        const attempt = this.lastAttemptedMove ?? optimisticMove?.target ?? previousPosition;
        const origin = optimisticMove?.from ?? previousPosition;
        const dx = Math.max(-1, Math.min(1, attempt.x - origin.x));
        const dy = Math.max(-1, Math.min(1, attempt.y - origin.y));
        const visualCenter = this.avatarPosition ?? previousPosition;
        const visualCellSize = this.getVisualCellSize();
        const trapX = (attempt.x - visualCenter.x) * visualCellSize;
        const trapY = (attempt.y - visualCenter.y) * visualCellSize;
        const collapseKey = attempt ? positionKey(attempt) : "";
        const collapsingTile = collapseKey ? this.tiles.get(collapseKey) ?? null : null;
        const collapsingTileView = collapseKey
            ? this.visibleTiles.find((tile) => tile.key === collapseKey) ?? null
            : null;
        const optimisticElapsed = optimisticMove ? performance.now() - optimisticMove.startedAt : HOP_DURATION_MS;
        const hasMoveDirection = dx !== 0 || dy !== 0;
        const settleDelay = optimisticMove
            ? Math.max(0, Math.min(FALL_SETTLE_MAX_MS, HOP_DURATION_MS - optimisticElapsed))
            : hasMoveDirection
              ? FALL_SETTLE_WITHOUT_HOP_MS
              : 0;
        const baseFallAngle = hasMoveDirection ? Math.atan2(dy, dx) : Math.random() * Math.PI * 2;
        const fallAngle =
            baseFallAngle +
            (Math.random() - 0.5) * (FALL_DIRECTION_RANDOMNESS_RADIANS * 2);
        const fallDistance =
            SITE_TUNING.relicRun.wrongTile.playerFallDistanceBasePx +
            Math.random() * SITE_TUNING.relicRun.wrongTile.playerFallDistanceRandomPx;
        const fallOffsetX = Math.cos(fallAngle) * fallDistance;
        const fallOffsetY = Math.sin(fallAngle) * fallDistance;
        const playerFallX = trapX + fallOffsetX;
        const playerFallY = trapY + fallOffsetY;
        const sideAngle =
            fallAngle +
            (Math.random() < 0.5 ? -1 : 1) *
                (FALL_TILE_DIRECTION_OFFSET_RADIANS + Math.random() * FALL_TILE_DIRECTION_OFFSET_RADIANS);
        const tileDistance =
            fallDistance +
            SITE_TUNING.relicRun.wrongTile.collapsingTileExtraDistanceBasePx +
            Math.random() * SITE_TUNING.relicRun.wrongTile.collapsingTileExtraDistanceRandomPx;
        const tileFallOffsetX = Math.cos(sideAngle) * tileDistance;
        const tileFallOffsetY = Math.sin(sideAngle) * tileDistance;
        const tileFallDuration = SITE_TUNING.relicRun.wrongTile.collapsingTileFallDurationMs;
        const collapseDelay = 0;
        const fallDelay = 0;
        const flareDelay = fallDelay + SITE_TUNING.relicRun.wrongTile.flareDelayAfterFallMs;
        const removeTileDelay =
            collapseDelay + tileFallDuration + SITE_TUNING.relicRun.wrongTile.removeCollapsedTileDelayMs;
        const cameraReturnDelay =
            flareDelay + SITE_TUNING.relicRun.wrongTile.cameraReturnDelayAfterFlareMs;
        const resetCompleteDelay =
            cameraReturnDelay + SITE_TUNING.relicRun.wrongTile.respawnDelayAfterCameraMs;

        if (collapseKey) {
            this.collapsedTileKeys.add(collapseKey);
        }

        this.optimisticMove = null;
        this.flare.style.setProperty("--flare-x", `${playerFallX}px`);
        this.flare.style.setProperty("--flare-y", `${playerFallY}px`);
        this.flare.classList.remove("is-active");
        this.pit.classList.remove("is-active");
        this.passageButton.classList.remove("is-mistake-flash");
        this.disableTiles(true);
        const avatarTrapStart = this.captureAvatarFrame();

        this.playAvatarAnimation(
            [
                avatarTrapStart,
                { transform: `translate(${trapX}px, ${trapY}px) scale(1)`, opacity: 1 }
            ],
            {
                duration: settleDelay,
                easing: "cubic-bezier(0.18, 0.84, 0.24, 1)",
                fill: "forwards"
            },
            false
        );

        this.scheduleAnimationStep(() => {
            this.avatarLayer.classList.add("is-submerged");
            if (collapsingTileView) {
                this.boardRenderer.startTileCollapse(
                    this.buildRenderTile(collapsingTileView),
                    tileFallOffsetX,
                    tileFallOffsetY,
                    tileFallDuration
                );
            }
            collapsingTile?.classList.add("is-collapsing");
        }, collapseDelay);

        this.scheduleAnimationStep(() => {
            const avatarFallStart = this.captureAvatarFrame();
            this.playAvatarAnimation(
                [
                    avatarFallStart,
                    {
                        transform: `translate(${trapX + fallOffsetX * 0.42}px, ${trapY + fallOffsetY * 0.42}px) scale(0.84, 0.74)`,
                        opacity: 0.8,
                        offset: 0.5
                    },
                    {
                        transform: `translate(${playerFallX}px, ${playerFallY}px) scale(0.02)`,
                        opacity: 0.02,
                        offset: 1
                    }
                ],
                {
                    duration: SITE_TUNING.relicRun.wrongTile.playerFallDurationMs,
                    easing: "cubic-bezier(0.26, 0.02, 0.16, 1)",
                    fill: "forwards"
                },
                false
            );
        }, fallDelay);

        this.scheduleAnimationStep(() => {
            this.passageButton.classList.remove("is-mistake-flash");
            void this.passageButton.offsetWidth;
            this.passageButton.classList.add("is-mistake-flash");
        }, Math.max(0, fallDelay - 40));

        this.scheduleAnimationStep(() => {
            this.flare.classList.remove("is-active");
            void this.flare.offsetWidth;
            this.flare.classList.add("is-active");
        }, flareDelay);

        this.scheduleAnimationStep(() => {
            if (!collapseKey) {
                return;
            }

            const tile = this.tiles.get(collapseKey);

            if (!tile) {
                return;
            }

            tile.remove();
            this.tiles.delete(collapseKey);
        }, removeTileDelay);

        this.scheduleAnimationStep(() => {
            this.syncTiles(player.visibleTiles, player.position);
            this.visualCenter = null;
            this.setCamera(player.position, true);
        }, cameraReturnDelay);

        this.scheduleAnimationStep(() => {
            this.flare.classList.remove("is-active");
            this.pit.classList.remove("is-active");
            this.applyAvatarAppearance(player);
            this.playAvatarDrop(RESPAWN_DURATION_MS);
            this.isResetAnimating = false;
            this.syncTiles(player.visibleTiles, player.position);
            this.disableTiles(false);
            this.clearPressedTiles();
            this.currentPlayer = player;
            this.lastAttemptedMove = null;
            this.lastDispatchedMoveKey = "";
            this.lastDispatchedMoveAt = 0;
            this.updateOverlay(player);
            this.updatePassageUi(player);
            this.scheduleAvatarVisibilityGuard("reset-respawn", RESPAWN_DURATION_MS + 48);
            this.notifyReadyForTiming("reset-respawn", player);

            if (this.pendingRoom) {
                const nextRoom = this.pendingRoom;
                this.pendingRoom = null;
                this.setRoom(nextRoom);
            }
        }, resetCompleteDelay);
    }

    private playAdvanceAnimation(player: PlayerMazeView) {
        this.cancelPathDrag();
        if (!this.currentPlayer) {
            this.lastHandledAdvanceId = player.lastEvent?.id ?? this.lastHandledAdvanceId;
            this.applyPlayerState(player);
            return;
        }

        this.isAdvanceAnimating = true;
        this.clearOptimisticMoveTimer();
        this.clearAnimationTimers();
        this.resetStageEffects();
        this.clearBufferedMove();
        this.lastHandledAdvanceId = player.lastEvent?.id ?? this.lastHandledAdvanceId;
        this.lastDispatchedMoveKey = "";
        this.lastDispatchedMoveAt = 0;
        this.finalizePendingMovePerf("move-advance", {
            progressIndex: player.progressIndex,
            levelNumber: player.levelNumber
        });

        const optimisticMove = this.optimisticMove;
        const sinkPosition = optimisticMove?.target ?? this.currentPlayer.position;
        const origin = optimisticMove?.from ?? this.currentPlayer.position;
        const dx = Math.max(-1, Math.min(1, sinkPosition.x - origin.x));
        const dy = Math.max(-1, Math.min(1, sinkPosition.y - origin.y));
        const hasMoveDirection = dx !== 0 || dy !== 0;
        const visualCenter = this.avatarPosition ?? sinkPosition;
        const visualCellSize = this.getVisualCellSize();
        const sinkX = (sinkPosition.x - visualCenter.x) * visualCellSize;
        const sinkY = (sinkPosition.y - visualCenter.y) * visualCellSize;
        const optimisticElapsed = optimisticMove ? performance.now() - optimisticMove.startedAt : HOP_DURATION_MS;
        const settleDelay = optimisticMove
            ? Math.max(0, Math.min(FALL_SETTLE_MAX_MS, HOP_DURATION_MS - optimisticElapsed))
            : hasMoveDirection
              ? FALL_SETTLE_WITHOUT_HOP_MS
              : 0;
        const fallAngle = hasMoveDirection ? Math.atan2(dy, dx) : -Math.PI / 2;
        const fallDistance =
            SITE_TUNING.relicRun.portalTransition.fallDistanceBasePx +
            Math.random() * SITE_TUNING.relicRun.portalTransition.fallDistanceRandomPx;
        const fallOffsetX = Math.cos(fallAngle) * fallDistance;
        const fallOffsetY = Math.sin(fallAngle) * fallDistance;
        const flareDelay = settleDelay + SITE_TUNING.relicRun.portalTransition.flareDelayAfterSettleMs;
        const fallDelay = settleDelay + SITE_TUNING.relicRun.portalTransition.fallDelayAfterSettleMs;
        const blackoutDelay =
            fallDelay +
            PORTAL_FALL_DURATION_MS +
            SITE_TUNING.relicRun.portalTransition.blackoutDelayAfterFallMs;
        const boardExitDelay =
            blackoutDelay +
            BLACKOUT_FADE_IN_MS +
            SITE_TUNING.relicRun.portalTransition.boardExitDelayAfterBlackoutMs;
        const boardSwapDelay =
            boardExitDelay + SITE_TUNING.relicRun.portalTransition.boardExitDurationMs;
        const boardEnterDelay =
            boardSwapDelay + SITE_TUNING.relicRun.portalTransition.boardEnterDelayAfterSwapMs;
        const entryDelay =
            boardEnterDelay + SITE_TUNING.relicRun.portalTransition.entryDelayAfterBoardEnterMs;
        const transitionCompleteDelay = entryDelay + ENTRY_FALL_DURATION_MS;
        const boardTravel = Math.max(this.stage.clientHeight * 0.82, visualCellSize * 4);
        const boardTravelX = Math.max(this.stage.clientWidth * 0.14, visualCellSize * 1.5);

        this.collapsedTileKeys.clear();
        this.optimisticMove = null;
        this.isPassageVisible = false;
        this.flare.style.setProperty("--flare-x", `${sinkX}px`);
        this.flare.style.setProperty("--flare-y", `${sinkY}px`);
        this.flare.classList.remove("is-active");
        this.pit.classList.remove("is-active");
        this.disableTiles(true);
        this.updatePassageUi(this.currentPlayer);
        const avatarPortalStart = this.captureAvatarFrame();

        this.playAvatarAnimation(
            [
                avatarPortalStart,
                { transform: `translate(${sinkX}px, ${sinkY}px) scale(1)`, opacity: 1 }
            ],
            {
                duration: settleDelay,
                easing: "cubic-bezier(0.18, 0.84, 0.24, 1)",
                fill: "forwards"
            },
            false
        );

        this.scheduleAnimationStep(() => {
            this.flare.classList.remove("is-active");
            void this.flare.offsetWidth;
            this.flare.classList.add("is-active");
        }, flareDelay);

        this.scheduleAnimationStep(() => {
            this.avatarLayer.classList.add("is-submerged");
            this.showLevelBestCallout(player);
            const avatarFallStart = this.captureAvatarFrame();
            this.playAvatarAnimation(
                [
                    avatarFallStart,
                    {
                        transform: `translate(${sinkX + fallOffsetX * 0.16}px, ${sinkY + fallOffsetY * 0.16 - 6}px) scale(0.92, 0.82)`,
                        opacity: 0.94,
                        offset: 0.28
                    },
                    {
                        transform: `translate(${sinkX + fallOffsetX * 0.36}px, ${sinkY + fallOffsetY * 0.36 - 10}px) scale(0.78, 0.66)`,
                        opacity: 0.8,
                        offset: 0.56
                    },
                    {
                        transform: `translate(${sinkX + fallOffsetX * 0.74}px, ${sinkY + fallOffsetY * 0.74 + FALL_DESTINATION_Y_OFFSET * 0.42}px) scale(0.38, 0.34)`,
                        opacity: 0.42,
                        offset: 0.82
                    },
                    {
                        transform: `translate(${sinkX + fallOffsetX}px, ${sinkY + fallOffsetY + FALL_DESTINATION_Y_OFFSET}px) scale(0.02)`,
                        opacity: 0.02,
                        offset: 1
                    }
                ],
                {
                    duration: PORTAL_FALL_DURATION_MS,
                    easing: "cubic-bezier(0.22, 0.04, 0.16, 1)",
                    fill: "forwards"
                },
                false
            );
        }, fallDelay);

        this.scheduleAnimationStep(() => {
            this.animateFallCurtain(1, BLACKOUT_FADE_IN_MS, "cubic-bezier(0.2, 0.86, 0.24, 1)");
        }, blackoutDelay);

        this.scheduleAnimationStep(() => {
            this.setBoardView(this.visualCenter ?? sinkPosition, {
                animate: true,
                shiftX: -boardTravelX,
                shiftY: -boardTravel,
                tiltDeg: -6,
                opacity: 0,
                durationMs: SITE_TUNING.relicRun.portalTransition.boardExitDurationMs,
                easing: "cubic-bezier(0.14, 0.82, 0.18, 1)"
            });
        }, boardExitDelay);

        this.scheduleAnimationStep(() => {
            this.applyLevelTint(player.levelNumber);
            this.applyEntryCorner(player);
            this.syncTiles(player.visibleTiles, player.position);
            const entryCamera = startRelicCamera(player.position, { width: this.stageWidth, height: this.stageHeight, cellSize: this.getVisualCellSize(), corner: this.entryCorner, bounds: this.courseBounds, placement: this.coursePlacement });
            this.setBoardView(entryCamera, {
                animate: false,
                shiftX: boardTravelX * 0.52,
                shiftY: boardTravel * 0.72,
                tiltDeg: 4,
                opacity: 0.22
            });
        }, boardSwapDelay);

        this.scheduleAnimationStep(() => {
            const entryCamera = startRelicCamera(player.position, { width: this.stageWidth, height: this.stageHeight, cellSize: this.getVisualCellSize(), corner: this.entryCorner, bounds: this.courseBounds, placement: this.coursePlacement });
            this.positionAvatar(player.position, entryCamera, false);
            this.setBoardView(entryCamera, {
                animate: true,
                shiftX: 0,
                shiftY: 0,
                tiltDeg: 0,
                opacity: 1,
                durationMs: SITE_TUNING.relicRun.portalTransition.boardEnterDurationMs,
                easing: "cubic-bezier(0.12, 0.84, 0.2, 1)"
            });
        }, boardEnterDelay);

        this.scheduleAnimationStep(() => {
            this.applyAvatarAppearance(player);
            this.playAvatarDrop(ENTRY_FALL_DURATION_MS);
            this.animateFallCurtain(
                0,
                SITE_TUNING.relicRun.portalTransition.curtainFadeOutMs,
                "cubic-bezier(0.12, 0.84, 0.2, 1)"
            );
        }, entryDelay);

        this.scheduleAnimationStep(() => {
            this.flare.classList.remove("is-active");
            this.pit.classList.remove("is-active");
            this.hideLevelBestCallout();
            this.applyAvatarAppearance(player);
            this.resetAvatarTransform();
            this.isAdvanceAnimating = false;
            this.isEntryAnimating = false;
            this.syncTiles(player.visibleTiles, player.position);
            this.disableTiles(false);
            this.clearPressedTiles();
            this.currentPlayer = player;
            this.lastAttemptedMove = null;
            this.lastDispatchedMoveKey = "";
            this.lastDispatchedMoveAt = 0;
            this.isPassageVisible = Boolean(this.passageText);
            this.updateOverlay(player);
            this.updatePassageUi(player);
            this.scheduleAvatarVisibilityGuard("advance-entry", ENTRY_FALL_DURATION_MS + 48);
            this.notifyReadyForTiming("advance-entry", player);

            if (this.pendingRoom) {
                const nextRoom = this.pendingRoom;
                this.pendingRoom = null;
                this.setRoom(nextRoom);
            }
        }, transitionCompleteDelay);
    }

    private disableTiles(disabled: boolean) {
        for (const tile of this.tiles.values()) {
            const blocked =
                disabled ||
                !this.boardInputEnabled ||
                !tile.classList.contains("is-reachable");
            tile.classList.remove("is-pressed");
            tile.dataset.blocked = String(blocked);
            tile.setAttribute("aria-disabled", blocked ? "true" : "false");
            tile.tabIndex = blocked ? -1 : 0;
            tile.disabled = false;
        }

        this.pressedTile = null;
        this.syncCanvasBoard();
    }

    private resetAvatarTransform() {
        this.avatarAnimationNonce += 1;
        this.avatar.getAnimations().forEach(animation => animation.cancel());
        this.avatarAnimation = null;
        this.avatarLayer.classList.remove("is-submerged");
        this.avatar.style.transform = "translate(0px, 0px) scale(1)";
        this.avatar.style.opacity = "1";
    }

    private playEntryAnimation(player: PlayerMazeView) {
        this.clearAnimationTimers();
        this.resetStageEffects();
        this.clearBufferedMove();
        this.isEntryAnimating = true;
        this.isPassageVisible = Boolean(this.passageText);
        this.disableTiles(true);
        this.applyAvatarAppearance(player);
        this.updatePassageUi(player);
        this.playAvatarDrop(ENTRY_FALL_DURATION_MS);

        this.scheduleAnimationStep(() => {
            this.isEntryAnimating = false;
            this.disableTiles(false);
            this.updatePassageUi(player);
            this.notifyReadyForTiming("entry-fall", player);
        }, ENTRY_FALL_DURATION_MS);
        this.scheduleAvatarVisibilityGuard("entry-fall", ENTRY_FALL_DURATION_MS + 48);
    }

    private showPassageText() {
        if (!this.passageText) {
            return;
        }

        this.isPassageVisible = true;
        this.updatePassageUi(this.currentPlayer);
    }

    private hidePassageTextForMove() {
        if (!this.isPassageVisible) {
            return;
        }

        this.isPassageVisible = false;
        this.updatePassageUi(this.currentPlayer);
    }

    private updateOverlay(player: PlayerMazeView | null) {
        if (player?.completed) {
            const personalBestMs = player.runPersonalBestMs ?? player.lastFinishedRunMs;
            const completionMessage =
                personalBestMs !== null
                    ? t("beatPersonalBest", { time: formatRunTime(personalBestMs) })
                    : t("escapedMessage");

            this.overlay.hidden = false;
            this.overlay.innerHTML = `
                <div class="overlay-message">
                    <div class="overlay-treasure" aria-hidden="true">&#128176;</div>
                    <h2>${escapeHtml(t("escapedTitle"))}</h2>
                    <p>${escapeHtml(completionMessage)}</p>
                    ${
                        player.canReplay
                            ? `<div class="overlay-actions">
                                    <button
                                        id="maze-replay-button"
                                        class="primary-button button-accent-teal"
                                        type="button"
                                        ${this.isReplayPending ? "disabled" : ""}
                                    >
                                        ${escapeHtml(t("tryAgain"))}
                                    </button>
                               </div>`
                            : ""
                    }
                </div>
            `;
            return;
        }

        this.overlay.hidden = true;
        this.overlay.innerHTML = "";
    }

    private handleDirectionalKeyDown(event: KeyboardEvent) {
        if (
            event.defaultPrevented ||
            event.altKey ||
            event.ctrlKey ||
            event.metaKey ||
            isDirectionalKeyControlTarget(event.target)
        ) {
            return;
        }

        const move = directionalMoveForKey(event.key);

        if (!move) {
            return;
        }

        if (this.tryMoveByDelta(move.delta, move.source) && event.cancelable) {
            event.preventDefault();
        }
    }

    private tryMoveByDelta(delta: GridPosition, source: string): boolean {
        const player = this.currentPlayer;

        if (!player?.isViewerControlled || player.completed) {
            return false;
        }

        const inputPosition = this.getInputPosition() ?? player.position;
        const target = {
            x: inputPosition.x + delta.x,
            y: inputPosition.y + delta.y
        };
        const tile = this.tiles.get(positionKey(target));

        if (!tile) {
            logMazeDebug("directional move target is outside the visible maze", {
                source,
                target
            });
            return false;
        }

        this.logTileEvent(source, tile);
        return this.tryMove(tile, source);
    }

    private handleTouchSwipePointerDown(event: PointerEvent) {
        this.suppressPointerUntilRelease = false;
        if (
            (event.pointerType === "mouse" && event.button !== 0) ||
            !event.isPrimary ||
            !this.currentPlayer?.isViewerControlled ||
            this.currentPlayer.completed ||
            this.isInteractiveStageControl(event.target) ||
            this.isEntryAnimating || this.isResetAnimating || this.isAdvanceAnimating
        ) {
            return;
        }

        this.touchSwipe = {
            pointerId: event.pointerId,
            startX: event.clientX,
            startY: event.clientY,
            handled: false,
            lastX: event.clientX,
            lastY: event.clientY,
            distanceSinceCommit: 0,
            cameraMoved: false
        };

        if (this.stage.setPointerCapture) {
            try {
                this.stage.setPointerCapture(event.pointerId);
            } catch {
                // Some synthetic test events and older mobile browsers do not expose capture for this pointer.
            }
        }
    }

    private handleTouchSwipePointerMove(event: PointerEvent) {
        if (!this.touchSwipe || event.pointerId !== this.touchSwipe.pointerId) {
            return;
        }

        if (this.tracePointerSegment(event) && event.cancelable) {
            event.preventDefault();
        }
    }

    private handleTouchSwipePointerUp(event: PointerEvent) {
        if (this.suppressPointerUntilRelease) {
            this.suppressPointerUntilRelease = false;
            this.suppressTouchClickUntil = performance.now() + TOUCH_SWIPE_CLICK_SUPPRESS_MS;
            if (event.cancelable) event.preventDefault();
            return;
        }
        if (!this.touchSwipe || event.pointerId !== this.touchSwipe.pointerId) {
            return;
        }

        this.tracePointerSegment(event);
        const handled = this.touchSwipe.handled || (event.pointerType === "touch" && this.tryTouchSwipeMove(event));
        this.releaseTouchSwipePointer(event);
        this.touchSwipe = null;

        if (handled) {
            // A held drag may outlive the last movement's suppression window.
            // Its release click must not select the tile now under the finger.
            this.suppressTouchClickUntil = performance.now() + TOUCH_SWIPE_CLICK_SUPPRESS_MS;
            if (event.cancelable) event.preventDefault();
        }
    }

    private handleTouchSwipePointerCancel(event: PointerEvent) {
        if (!this.touchSwipe || event.pointerId !== this.touchSwipe.pointerId) {
            return;
        }

        this.releaseTouchSwipePointer(event);
        this.touchSwipe = null;
        this.clearBufferedMove();
    }

    private cancelPathDrag() {
        const drag = this.touchSwipe;
        if (!drag) return;
        this.suppressPointerUntilRelease = true;
        try {
            if (this.stage.hasPointerCapture(drag.pointerId)) this.stage.releasePointerCapture(drag.pointerId);
        } catch { /* Capture may already have ended after cancellation. */ }
        this.touchSwipe = null;
        this.suppressTouchClickUntil = performance.now() + TOUCH_SWIPE_CLICK_SUPPRESS_MS;
    }

    private tracePointerSegment(event: PointerEvent): boolean {
        const drag = this.touchSwipe;
        if (!drag || this.isResetAnimating || this.isAdvanceAnimating) return false;
        const dx = event.clientX - drag.lastX, dy = event.clientY - drag.lastY;
        if (Math.hypot(dx, dy) < 1) return false;
        const samples = Math.min(64, Math.max(1, Math.ceil(Math.hypot(dx, dy) / (this.getVisualCellSize() / 4))));
        let handled = false;
        // Sample the actual finger segment, including intermediate cells. Never
        // invent a route to a distant tile or silently step around a wrong word.
        for (let sample = 1; sample <= samples; sample += 1) {
            const x = drag.lastX + dx * sample / samples, y = drag.lastY + dy * sample / samples;
            drag.distanceSinceCommit += Math.hypot(dx, dy) / samples;
            // Count real finger travel, including after a slow camera pan. A
            // word sliding under a nearly stationary finger is not a new step.
            if (drag.cameraMoved && drag.distanceSinceCommit < this.getVisualCellSize() * SITE_TUNING.relicRun.playerMotion.tracedCameraStepDistanceCells) continue;
            const tile = this.findTileButtonFromPoint(x, y);
            const origin = this.getInputPosition();
            if (!tile || !origin || tile.classList.contains("is-fogged")) continue;
            const target = { x: Number(tile.dataset.moveX), y: Number(tile.dataset.moveY) };
            if (manhattanDistance(target, origin) !== 1) continue;
            if ((target.x - origin.x) * dx + (target.y - origin.y) * dy <= 0) continue;
            if (this.tryMove(tile, "drag-path")) {
                handled = true;
                drag.distanceSinceCommit = 0;
            }
        }
        drag.lastX = event.clientX;
        drag.lastY = event.clientY;
        if (handled) {
            drag.handled = true;
            this.suppressTouchClickUntil = performance.now() + TOUCH_SWIPE_CLICK_SUPPRESS_MS;
            this.clearPressedTiles();
        }
        return handled;
    }

    private tryTouchSwipeMove(event: PointerEvent): boolean {
        const touchSwipe = this.touchSwipe;

        if (!touchSwipe || touchSwipe.handled) {
            return false;
        }

        const move = directionalMoveForSwipe(event.clientX - touchSwipe.startX, event.clientY - touchSwipe.startY);

        if (!move) {
            return false;
        }

        const handled = this.tryMoveByDelta(move.delta, move.source);

        if (!handled) {
            return false;
        }

        touchSwipe.handled = true;
        this.suppressTouchClickUntil = performance.now() + TOUCH_SWIPE_CLICK_SUPPRESS_MS;
        this.clearPressedTiles();
        return true;
    }

    private releaseTouchSwipePointer(event: PointerEvent) {
        if (this.stage.releasePointerCapture) {
            try {
                this.stage.releasePointerCapture(event.pointerId);
            } catch {
                // Pointer capture may already be gone after a browser-level cancellation.
            }
        }
    }

    private isInteractiveStageControl(target: EventTarget | null): boolean {
        const element =
            target instanceof Element
                ? target
                : target instanceof Node
                  ? target.parentElement
                  : null;

        return Boolean(element?.closest("#maze-passage-button, #maze-replay-button"));
    }

    private consumeSuppressedTouchClick(event: MouseEvent): boolean {
        if (performance.now() > this.suppressTouchClickUntil) {
            return false;
        }

        if (event.cancelable) {
            event.preventDefault();
        }
        event.stopPropagation();
        return true;
    }

    private handleStagePointer(event: PointerEvent | MouseEvent, source: string) {
        if (this.suppressPointerUntilRelease) return;
        if (this.isInteractiveStageControl(event.target)) return;
        if (source === "stage-click" && this.consumeSuppressedTouchClick(event)) {
            return;
        }

        if (!this.currentPlayer?.isViewerControlled) {
            return;
        }

        if (this.findTileButton(event.target)) {
            return;
        }

        const tile = this.findTileButtonFromPoint(event.clientX, event.clientY);

        if (!tile) {
            const topElement = document.elementFromPoint(event.clientX, event.clientY);

            logMazeDebug("stage pointer without tile", {
                source,
                x: event.clientX,
                y: event.clientY,
                topElement: topElement instanceof HTMLElement ? topElement.className : String(topElement)
            });
            return;
        }

        if (event.cancelable) {
            event.preventDefault();
        }

        this.logTileEvent(source, tile);

        if (source === "stage-pointerdown") {
            if (tile.dataset.blocked === "true") {
                return;
            }

            this.setPressedTile(tile);
        } else if (source === "stage-click") {
            this.clearPressedTiles();
        }

        this.tryMove(tile, source);
    }

    private findTileButton(target: EventTarget | null): HTMLButtonElement | null {
        const element =
            target instanceof Element
                ? target
                : target instanceof Node
                  ? target.parentElement
                  : null;

        if (!element) {
            return null;
        }

        const candidate = element.closest(".maze-tile");

        return candidate instanceof HTMLButtonElement ? candidate : null;
    }

    private findTileButtonFromPoint(clientX: number, clientY: number): HTMLButtonElement | null {
        const directHit = document.elementFromPoint(clientX, clientY);

        if (directHit instanceof HTMLElement) {
            const candidate = directHit.closest(".maze-tile");

            if (candidate instanceof HTMLButtonElement) {
                return candidate;
            }
        }

        return null;
    }

    private setPressedTile(tile: HTMLButtonElement | null) {
        if (this.pressedTile === tile) {
            return;
        }

        this.pressedTile?.classList.remove("is-pressed");
        this.pressedTile = tile;
        this.pressedTile?.classList.add("is-pressed");
        // Let the DOM hit target carry the brief press affordance so pointerdown can flow
        // straight into the optimistic hop without paying a whole canvas board resync first.
    }

    private clearPressedTiles() {
        if (!this.pressedTile) {
            return;
        }

        this.pressedTile?.classList.remove("is-pressed");
        this.pressedTile = null;
    }

    private getPassagePreview(passageText: string, targetWordIndex: number): PassagePreview {
        if (!this.passagePreviewCache || this.passagePreviewCache.sourceText !== passageText) {
            this.passagePreviewCache = createPassagePreviewCache(passageText);
        }

        return buildPassagePreview(this.passagePreviewCache, targetWordIndex);
    }

    private logTileEvent(eventName: string, tile: HTMLButtonElement) {
        logMazeDebug("tile event", {
            eventName,
            tileKey: tile.dataset.tileKey ?? "",
            blocked: tile.dataset.blocked === "true",
            reachable: tile.classList.contains("is-reachable"),
            nextStep: tile.dataset.nextStep === "true",
            word: tile.firstElementChild?.textContent ?? ""
        });
    }
}

export const createMazeView = (root: HTMLElement, options: MazeViewOptions): MazeView =>
    new MazeView(root, options);
